import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { track } from "../lib/mixpanel"

let sharedConsumer = null
function getConsumer() {
  if (!sharedConsumer) sharedConsumer = createConsumer()
  return sharedConsumer
}

export default class extends Controller {
  static targets = [
    "stepRow", "customStepRow",
    "stepUpdateModal", "stepUpdateForm", "modalTitle",
    "stepNameField", "stepNameInput",
    "responsiblesField", "responsiblesSelect",
    "completedCheckbox", "dateInput", "dueDateField", "dueDateInput", "dueDateLabel",
    "feasibilityDaysField", "feasibilityDaysInput", "feasibilityDateDisplay",
    "contractClosingDaysField", "contractClosingDaysInput", "contractClosingDateDisplay",
    "reaField", "reaInput",
    "notesInput", "fileInput", "fileList", "dropZone",
    "sequenceInput", "stepIdInput", "attachmentIdsInput", "isCustomInput", "completionTypeInput",
    "removeBtn", "completionDateLabel",
    "reminderSection", "reminderDateInput", "reminderHourInput", "reminderMinutesInput",
    "reminderAmpmInput", "reminderTimezoneInput", "reminderInviteListInput", "reminderCommentsInput",
    "inviteeDropdownWrapper", "inviteeDropdown", "inviteePlaceholder", "inviteeChips",
    "addCustomStepModal", "addCustomStepForm",
    "customStepNameInput", "customStepResponsiblesSelect", "ddTypeRadio",
    "responsibleDropdownWrapper", "responsibleDropdown", "responsiblePlaceholder", "responsibleChips",
    "editResponsibleDropdownWrapper", "editResponsibleDropdown", "editResponsiblePlaceholder", "editResponsibleChips",
    "customStepsContainer",
    "historyLogsModal", "historyLog", "historyLogSection", "deleteLogConfirmModal",
    "activityTextarea",
    // Property modal targets
    "addPropertyModal", "addPropertyForm", "addPropertyTitle",
    "propertyTypeInput", "propertyIdInput",
    "ppNameInput", "ppStatusInput", "ppAcresInput", "ppRsfInput",
    "ppAddressInput", "ppCityInput", "ppStateInput", "ppCountryInput", "ppZipcodeInput",
    "ppPurchasePriceInput", "ppLeaseRateInput", "ppCamInput", "ppLeaseTermInput",
    "ppNotesInput", "ppFileInput", "ppFileList", "ppDropZone",
    "leaseFields", "leaseRateFields", "purchaseFields", "acresField", "deletePropertyBtn", "addPropertyDialog",
    // SS modals
    "ssSignInstructionsModal", "neverShowSsCheckbox",
    "remainingMembersModal", "remainingMembersForm", "memberInput",
    "remindUsersModal", "remindNoteInput", "remindUserCheckbox", "remindSelectAll", "remindSelectedCount",
    "requestSigsModal", "requestSigsForm",
    "signPpModal", "signPpTitle", "signPpTypeInput", "signPpNotesInput", "sigRow",
    // Confirmation modals
    "uncheckConfirmModal", "removeStepConfirmModal", "deletePropertyConfirmModal", "unselectPpConfirmModal", "unsignConfirmModal",
    // Validation modals
    "pmMustSelectModal", "onlySignerCanUnsignModal",
    // View attachments modal
    "viewAttachmentsModal", "viewAttachmentsTitle", "viewAttachmentsList", "viewAttachmentsDialog",
    // Select preferred property modal
    "selectPpModal", "selectPpPropertySelect", "selectPpNotesInput",
    // Inline update targets
    "lastRemindedText", "purchaseTableBody", "leaseTableBody",
    "ppRow", "ppCheckbox", "signaturesSection", "signaturesPlaceholder",
    "preferredPropertySection", "preferredPropertyPlaceholder",
    // Edit cost modal
    "editCostModal", "editCostTitle", "editCostTypeInput", "editCostPpIdInput",
    "editCostAmountField", "editCostAmountInput",
    "onSiteSubFields", "editCostSlabInput", "editCostEarthworkInput",
    "editCostSiteUtilitiesInput", "editCostAsphaltInput",
    "editCostFencingInput", "editCostLandscapingInput",
    "rentalRateSubFields", "editCostLeaseRateInput", "editCostRsfInput",
    "editCostLeaseTermInput", "editCostCamInput", "rentalRateTotalDisplay",
    "tiAmountField", "editCostTiInput",
    "dpAmountField", "editCostDpInput",
    "editCostFileInput", "editCostFileList", "editCostDropZone",
    "onSiteTotalDisplay",
    // FCW / NRS modals
    "fcwModal", "fcwForm", "fcwResponse", "fcwComment", "fcwStatusInput",
    "fcwFileInput", "fcwFileList", "fcwDropZone", "fcwCostInput",
    "nrsModal", "nrsForm", "nrsResponse", "nrsComment",
    "nrsDateInput", "nrsPropertyUseInput", "nrsAcresInput", "nrsTitleInput",
    "nrsPropertyNumberInput", "nrsStatusInput", "nrsFileInput", "nrsFileList", "nrsDropZone",
    "nrsWaterSection", "nrsWaterTable", "nrsCompanyTable", "nrsDistrictTable",
    "nrsSigName", "nrsSigConfirmModal", "nrsSigConfirmTitle"
  ]

  static values = {
    projectId: Number,
    stageId: Number,
    stageName: String,
    currentUserId: Number,
    currentUserIsRea: Boolean,
    currentUserIsSp: Boolean,
    currentUserName: String
  }

  connect() {
    track("RE Step Viewed", { step_type: this.element.dataset.stepType, stage: this.element.dataset.stage })
    // Remove ?s= from URL bar so it stays clean
    const url = new URL(window.location)
    if (url.searchParams.has("s")) {
      url.searchParams.delete("s")
      history.replaceState(null, "", url.toString())
    }

    this._modalOpen = false
    this._openModalCount = 0
    this._selectedResponsibles = new Map()
    this._uploadedAttachmentIds = []
    this._uploadedAttachmentNames = []
    this._uploadedAttachmentUrls = []
    this._ppAttachmentIds = []
    this._outsideClickHandler = (e) => {
      if (this.hasResponsibleDropdownWrapperTarget && !this.responsibleDropdownWrapperTarget.contains(e.target)) {
        if (this.hasResponsibleDropdownTarget) this.responsibleDropdownTarget.classList.add("hidden")
      }
      if (this.hasEditResponsibleDropdownWrapperTarget && !this.editResponsibleDropdownWrapperTarget.contains(e.target)) {
        if (this.hasEditResponsibleDropdownTarget) this.editResponsibleDropdownTarget.classList.add("hidden")
      }
      if (this.hasInviteeDropdownWrapperTarget && !this.inviteeDropdownWrapperTarget.contains(e.target)) {
        if (this.hasInviteeDropdownTarget) this.inviteeDropdownTarget.classList.add("hidden")
      }
    }
    document.addEventListener("click", this._outsideClickHandler)
    this._fcwAttachmentIds = []
    this._nrsAttachmentIds = []
    this._costAttachmentIds = []
    this._subscribeToChannel()
  }

  disconnect() {
    if (this._subscription) {
      this._subscription.unsubscribe()
    }
    if (this._outsideClickHandler) {
      document.removeEventListener("click", this._outsideClickHandler)
    }
  }

  // ── ActionCable ─────────────────────────────────────────────────────

  _subscribeToChannel() {
    this._subscription = getConsumer().subscriptions.create("RealEstateStepUpdatesChannel", {
      received: (data) => {
        if (data.project_id == this.projectIdValue && data.user_id != this.currentUserIdValue && !this._modalOpen) {
          window.location.reload()
        }
      }
    })
  }

  // ── Step checkbox toggles ───────────────────────────────────────────

  toggleComplete(event) {
    const checkbox = event.currentTarget
    const row = checkbox.closest("tr")
    const sequence = checkbox.dataset.sequence
    const completed = checkbox.checked

    if (completed) {
      const today = new Date().toISOString().split("T")[0]
      this._post(`/real_estate/update_row/${this.projectIdValue}`, {
        s: this.stageIdValue,
        sequence: sequence,
        completed: "true",
        date: today
      }).then(() => {
        this._updateRowCells(row, { completed: true, date: today, userName: this.currentUserNameValue })
        this._showToast(`${row.dataset.stepName} marked as completed`)
      })
    } else {
      this._pendingUncheck = { type: "standard", checkbox, row, sequence }
      checkbox.checked = true
      this.uncheckConfirmModalTarget.classList.remove("hidden")
    }
  }

  toggleNa(event) {
    const checkbox = event.currentTarget
    const row = checkbox.closest("tr")
    const sequence = checkbox.dataset.sequence
    const na = checkbox.checked

    const data = { s: this.stageIdValue, sequence, na: na ? "true" : "false" }
    if (na) data.completed = "false"

    this._post(`/real_estate/update_row/${this.projectIdValue}`, data).then(() => {
      this._updateNaRow(row, na)
      this._showToast(`${row.dataset.stepName} marked as ${na ? "N/A" : "required"}`)
    })
  }

  toggleCustomComplete(event) {
    const checkbox = event.currentTarget
    const row = checkbox.closest("tr")
    const stepId = checkbox.dataset.stepId
    const completed = checkbox.checked

    if (completed) {
      const today = new Date().toISOString().split("T")[0]
      this._post(`/real_estate/update_custom_step/${this.projectIdValue}`, {
        s: this.stageIdValue,
        step_id: stepId,
        completed: "true",
        date: today
      }).then(() => {
        this._updateRowCells(row, { completed: true, date: today, userName: this.currentUserNameValue })
        this._showToast(`${row.dataset.stepName} marked as completed`)
      })
    } else {
      this._pendingUncheck = { type: "custom", checkbox, row, stepId }
      checkbox.checked = true
      this.uncheckConfirmModalTarget.classList.remove("hidden")
    }
  }

  toggleCustomNa(event) {
    const checkbox = event.currentTarget
    const row = checkbox.closest("tr")
    const stepId = checkbox.dataset.stepId
    const na = checkbox.checked

    const data = { s: this.stageIdValue, step_id: stepId, na: na ? "true" : "false" }
    if (na) data.completed = "false"

    this._post(`/real_estate/update_custom_step/${this.projectIdValue}`, data).then(() => {
      this._updateNaRow(row, na)
      this._showToast(`${row.dataset.stepName} marked as ${na ? "N/A" : "required"}`)
    })
  }

  // ── Uncheck confirmation modal ──────────────────────────────────────

  cancelUncheck() {
    this._pendingUncheck = null
    this.uncheckConfirmModalTarget.classList.add("hidden")
  }

  confirmUncheck() {
    const pending = this._pendingUncheck
    if (!pending) return

    this.uncheckConfirmModalTarget.classList.add("hidden")
    pending.checkbox.checked = false

    const promise = pending.type === "standard"
      ? this._post(`/real_estate/remove_row/${this.projectIdValue}`, {
          s: this.stageIdValue,
          sequence: pending.sequence
        })
      : this._post(`/real_estate/uncomplete_custom_row/${this.projectIdValue}`, {
          s: this.stageIdValue,
          step_id: pending.stepId
        })

    promise.then(() => {
      this._updateRowCells(pending.row, { completed: false, date: "" })
      this._showToast(`${pending.row.dataset.stepName} marked as uncomplete`)
    })

    this._pendingUncheck = null
  }

  // ── Step update modal ───────────────────────────────────────────────

  openStepModal(event) {
    const row = event.currentTarget.closest("tr")
    if (!row) return
    if (row.dataset.na === "true") return

    this._modalOpen = true
    this._activeRow = row
    this._uploadedAttachmentIds = []
    this._uploadedAttachmentNames = []
    this._uploadedAttachmentUrls = []

    const isCustom = row.dataset.isCustom === "true"
    const sequence = row.dataset.sequence
    const stepId = row.dataset.stepId
    const stepName = row.dataset.stepName
    const completed = row.dataset.completed === "true"
    const notes = row.dataset.notes || ""
    const date = row.dataset.date || ""
    const dueDate = row.dataset.dueDate || ""
    const attachmentIds = row.dataset.attachmentIds || ""
    const formType = row.dataset.formType

    // FCW / NRS custom form types redirect
    if (formType === "custom") {
      if (stepName === "Feasibility Cost Worksheet") {
        this.openFcw()
      } else if (stepName === "NRS Review") {
        this.openNrs()
      }
      return
    }

    this.modalTitleTarget.textContent = stepName
    this.isCustomInputTarget.value = isCustom ? "true" : "false"
    this.sequenceInputTarget.value = sequence || ""
    this.stepIdInputTarget.value = stepId || ""
    this.completedCheckboxTarget.checked = completed
    this._setDatepicker(this.dateInputTarget, date)
    this.notesInputTarget.value = notes
    this.attachmentIdsInputTarget.value = attachmentIds

    // Due date
    if (this.hasDueDateInputTarget) this._setDatepicker(this.dueDateInputTarget, dueDate)

    // Show/hide fields based on context
    this._showField(this.stepNameFieldTarget, isCustom)
    this._showField(this.responsiblesFieldTarget, isCustom)
    if (isCustom) {
      this.stepNameInputTarget.value = stepName
      // Populate edit responsibles dropdown chips
      this._editSelectedResponsibles = new Map()
      const responsibles = (row.dataset.responsibles || "").split("||").filter(Boolean)
      responsibles.forEach(role => {
        const label = this._responsibleLabel(role)
        this._editSelectedResponsibles.set(role, label)
      })
      this._renderEditResponsibleChips()
      if (this.hasEditResponsibleDropdownTarget) this.editResponsibleDropdownTarget.classList.add("hidden")
    }

    // Remove button: only for custom steps created by current user
    const creatorId = row.dataset.creatorId
    const showRemove = isCustom && creatorId && parseInt(creatorId) === this.currentUserIdValue
    this.removeBtnTarget.classList.toggle("hidden", !showRemove)

    // DD-specific fields
    const seq = parseInt(sequence)
    const showFeasibility = stepName === "Contract Effective Date"
    const showClosing = stepName === "Contract Effective Date"
    this._showField(this.feasibilityDaysFieldTarget, showFeasibility)
    this._showField(this.contractClosingDaysFieldTarget, showClosing)
    // Reset days inputs and calculated date displays
    if (this.hasFeasibilityDaysInputTarget) this.feasibilityDaysInputTarget.value = ""
    if (this.hasContractClosingDaysInputTarget) this.contractClosingDaysInputTarget.value = ""
    if (this.hasFeasibilityDateDisplayTarget) this.feasibilityDateDisplayTarget.textContent = "Feasibility Period End Due Date:"
    if (this.hasContractClosingDateDisplayTarget) this.contractClosingDateDisplayTarget.textContent = "Contract Closing Due Date:"

    if (this.hasReminderSectionTarget) {
      const showReminder = this.stageNameValue === "Due Diligence" &&
        ["Contract Closing Date", "Feasibility Period End Date", "Team Review Date"].includes(stepName)
      this._showField(this.reminderSectionTarget, showReminder)

      // Populate reminder fields from saved data
      if (this.hasReminderDateInputTarget) this._setDatepicker(this.reminderDateInputTarget, row.dataset.reminderDate || "")
      if (this.hasReminderHourInputTarget) this.reminderHourInputTarget.value = row.dataset.reminderHour || "9"
      if (this.hasReminderMinutesInputTarget) this.reminderMinutesInputTarget.value = row.dataset.reminderMinutes || "00"
      if (this.hasReminderAmpmInputTarget) this.reminderAmpmInputTarget.value = row.dataset.reminderAmpm || "AM"
      if (this.hasReminderTimezoneInputTarget && row.dataset.reminderTimeZone) this.reminderTimezoneInputTarget.value = row.dataset.reminderTimeZone
      if (this.hasReminderCommentsInputTarget) this.reminderCommentsInputTarget.value = row.dataset.reminderComments || ""

      // Populate invitees from existing data
      this._selectedInvitees = new Map()
      const inviteList = (row.dataset.reminderInviteList || "").split(" ").filter(Boolean)
      inviteList.forEach(email => this._selectedInvitees.set(email, email))
      this._renderInviteeChips()
      if (this.hasInviteeDropdownTarget) this.inviteeDropdownTarget.classList.add("hidden")
    }

    // Show due date for DD and Negotiations, but hide for specific steps
    let showDueDate = this.stageNameValue === "Due Diligence" || this.stageNameValue === "Negotiations"
    if (stepName === "Acquisition Work Order Received" || stepName === "Contract Effective Date") {
      showDueDate = false
    }
    this._showField(this.dueDateFieldTarget, showDueDate)
    if (this.hasDueDateLabelTarget) {
      const ddType = row.dataset.ddType
      const isOrderDate = this.stageNameValue === "Negotiations" ||
        (this.stageNameValue === "Due Diligence" && (seq >= 7 || (isCustom && ddType !== "contract_date")))
      this.dueDateLabelTarget.textContent = isOrderDate ? "Order Date" : "Due Date"
    }

    // Completion date label
    if (this.hasCompletionDateLabelTarget) {
      if (stepName === "Acquisition Work Order Received") {
        this.completionDateLabelTarget.textContent = "Assigned Date"
      } else if (stepName === "Contract Effective Date") {
        this.completionDateLabelTarget.textContent = "Effective Date"
      } else {
        this.completionDateLabelTarget.textContent = "Completion Date"
      }
    }

    // REA selector
    if (this.hasReaFieldTarget) {
      this._showField(this.reaFieldTarget, stepName === "Wire Transfer Information")
    }

    // File list — show existing attachments as removable chips
    this.fileListTarget.innerHTML = ""
    this._existingAttachmentIds = attachmentIds ? attachmentIds.split(" ").filter(Boolean) : []
    const attachmentNames = (row.dataset.attachmentNames || "").split("||").filter(Boolean)
    attachmentNames.forEach((name, i) => {
      const attId = this._existingAttachmentIds[i]
      this.fileListTarget.appendChild(this._createFileChip(name, {
        removable: true,
        attachmentId: attId,
        idsArray: this._existingAttachmentIds
      }))
    })

    this.stepUpdateModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  openCustomStepModal(event) {
    this.openStepModal(event)
  }

  closeStepModal() {
    this._modalOpen = false
    this.stepUpdateModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  stopPropagation(event) {
    event.stopPropagation()
    // Close responsible dropdowns if clicking outside them
    if (this.hasResponsibleDropdownWrapperTarget && !this.responsibleDropdownWrapperTarget.contains(event.target)) {
      if (this.hasResponsibleDropdownTarget) this.responsibleDropdownTarget.classList.add("hidden")
    }
    if (this.hasEditResponsibleDropdownWrapperTarget && !this.editResponsibleDropdownWrapperTarget.contains(event.target)) {
      if (this.hasEditResponsibleDropdownTarget) this.editResponsibleDropdownTarget.classList.add("hidden")
    }
    if (this.hasInviteeDropdownWrapperTarget && !this.inviteeDropdownWrapperTarget.contains(event.target)) {
      if (this.hasInviteeDropdownTarget) this.inviteeDropdownTarget.classList.add("hidden")
    }
  }

  calculateDaysDate() {
    const effectiveDate = this.dateInputTarget.value // "YYYY-MM-DD"
    if (!effectiveDate) {
      if (this.hasFeasibilityDateDisplayTarget) this.feasibilityDateDisplayTarget.textContent = "Feasibility Period End Due Date:"
      if (this.hasContractClosingDateDisplayTarget) this.contractClosingDateDisplayTarget.textContent = "Contract Closing Due Date:"
      return
    }

    const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

    if (this.hasFeasibilityDaysInputTarget && this.hasFeasibilityDateDisplayTarget) {
      const days = parseInt(this.feasibilityDaysInputTarget.value)
      if (days > 0) {
        const date = new Date(effectiveDate + "T00:00:00")
        date.setDate(date.getDate() + days)
        const formatted = `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`
        this.feasibilityDateDisplayTarget.innerHTML = `Feasibility Period End Due Date: ${formatted} <em>(${weekdays[date.getDay()]})</em>`
      } else {
        this.feasibilityDateDisplayTarget.textContent = "Feasibility Period End Due Date:"
      }
    }

    if (this.hasContractClosingDaysInputTarget && this.hasContractClosingDateDisplayTarget) {
      const days = parseInt(this.contractClosingDaysInputTarget.value)
      if (days > 0) {
        const date = new Date(effectiveDate + "T00:00:00")
        date.setDate(date.getDate() + days)
        const formatted = `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`
        this.contractClosingDateDisplayTarget.innerHTML = `Contract Closing Due Date: ${formatted} <em>(${weekdays[date.getDay()]})</em>`
      } else {
        this.contractClosingDateDisplayTarget.textContent = "Contract Closing Due Date:"
      }
    }
  }

  submitStepUpdate(event) {
    event.preventDefault()

    const isCustom = this.isCustomInputTarget.value === "true"
    const formData = {
      s: this.stageIdValue,
      completed: this.completedCheckboxTarget.checked ? "true" : "false",
      date: this.dateInputTarget.value,
      notes: this.notesInputTarget.value,
      attachment_ids: this._buildAttachmentIds()
    }

    if (this.hasDueDateInputTarget) formData.due_date = this.dueDateInputTarget.value

    if (isCustom) {
      formData.step_id = this.stepIdInputTarget.value
      formData.step_name = this.stepNameInputTarget.value
      formData.responsibles = this._getSelectedResponsibles()
      if (this.stageNameValue === "Due Diligence") {
        formData.is_order_date = "true"
      }
      this._post(`/real_estate/update_custom_step/${this.projectIdValue}`, formData).then(r => r.json()).then(json => {
        const row = this._activeRow
        const completed = formData.completed === "true"
        this._updateRowCells(row, {
          completed,
          date: formData.date,
          userName: this.currentUserNameValue,
          notes: formData.notes,
          dueDate: formData.due_date
        })
        if (formData.step_name) row.dataset.stepName = formData.step_name
        this._syncRowAttachments(row, json.attachments)
        this.closeStepModal()
        this._showToast("Step updated")
      })
    } else {
      formData.sequence = this.sequenceInputTarget.value
      formData.step_name = this.hasStepNameInputTarget ? this.stepNameInputTarget.value : undefined

      // DD specific
      if (this.hasFeasibilityDaysInputTarget && this.feasibilityDaysInputTarget.value) {
        formData.feasibility_days = this.feasibilityDaysInputTarget.value
      }
      if (this.hasContractClosingDaysInputTarget && this.contractClosingDaysInputTarget.value) {
        formData.contract_closing_days = this.contractClosingDaysInputTarget.value
      }
      if (this.hasReaInputTarget && this.reaInputTarget.value) {
        formData.real_estate_analyst = this.reaInputTarget.value
      }

      // Reminder fields
      if (this.hasReminderDateInputTarget && this.reminderDateInputTarget.value) {
        formData.reminder_date = this.reminderDateInputTarget.value
        formData.reminder_hour = this.reminderHourInputTarget.value
        formData.reminder_minutes = this.reminderMinutesInputTarget.value
        formData.reminder_ampm = this.reminderAmpmInputTarget.value
        formData.reminder_time_zone = this.reminderTimezoneInputTarget.value
        formData.reminder_invite_list = this.reminderInviteListInputTarget.value
        formData.reminder_comments = this.reminderCommentsInputTarget.value
      }

      this._post(`/real_estate/update_row/${this.projectIdValue}`, formData).then(r => r.json()).then(json => {
        const row = this._activeRow
        const completed = formData.completed === "true"
        this._updateRowCells(row, {
          completed,
          date: formData.date,
          userName: this.currentUserNameValue,
          notes: formData.notes,
          dueDate: formData.due_date
        })
        this._syncRowAttachments(row, json.attachments)

        // Sync reminder data attributes
        row.dataset.reminderDate = formData.reminder_date || ""
        row.dataset.reminderHour = formData.reminder_hour || ""
        row.dataset.reminderMinutes = formData.reminder_minutes || ""
        row.dataset.reminderAmpm = formData.reminder_ampm || ""
        row.dataset.reminderTimeZone = formData.reminder_time_zone || ""
        row.dataset.reminderInviteList = formData.reminder_invite_list || ""
        row.dataset.reminderComments = formData.reminder_comments || ""

        // Update target step rows for feasibility/closing days calculations
        if (formData.feasibility_days && formData.date) {
          const d = new Date(formData.date + "T00:00:00")
          d.setDate(d.getDate() + parseInt(formData.feasibility_days))
          const newDue = d.toISOString().split("T")[0]
          const fpRow = this._findStepRow("Feasibility Period End Date")
          if (fpRow) this._updateRowCells(fpRow, { dueDate: newDue })
        }
        if (formData.contract_closing_days && formData.date) {
          const d = new Date(formData.date + "T00:00:00")
          d.setDate(d.getDate() + parseInt(formData.contract_closing_days))
          const newDue = d.toISOString().split("T")[0]
          const ccRow = this._findStepRow("Contract Closing Date")
          if (ccRow) this._updateRowCells(ccRow, { dueDate: newDue })
        }

        this.closeStepModal()
        this._showToast("Step updated")
      })
    }
  }

  removeStep() {
    const isCustom = this.isCustomInputTarget.value === "true"
    if (!isCustom) return
    this.removeStepConfirmModalTarget.classList.remove("hidden")
  }

  cancelRemoveStep() {
    this.removeStepConfirmModalTarget.classList.add("hidden")
  }

  confirmRemoveStep() {
    this.removeStepConfirmModalTarget.classList.add("hidden")
    const stepId = this.stepIdInputTarget.value
    const row = this._activeRow

    // Close modals and remove row immediately
    this.closeStepModal()
    if (row) row.remove()
    this._showToast("Custom step removed successfully")

    this._post(`/real_estate/remove_custom_step/${this.projectIdValue}`, {
      step_id: stepId,
      s: this.stageIdValue
    }).catch(err => console.error("Remove step failed:", err))
  }

  // ── Add Custom Step modal ───────────────────────────────────────────

  openAddCustomStep(event) {
    this._modalOpen = true
    this._selectedResponsibles = new Map()
    if (this.hasCustomStepNameInputTarget) this.customStepNameInputTarget.value = ""
    this._renderResponsibleChips()
    if (this.hasResponsibleDropdownTarget) this.responsibleDropdownTarget.classList.add("hidden")
    this.addCustomStepModalTarget.classList.remove("hidden")
    this._lockScroll()

    // Pre-select DD type from data attribute if present
    if (event && event.currentTarget.dataset.ddType) {
      this.ddTypeRadioTargets.forEach(r => {
        r.checked = r.value === event.currentTarget.dataset.ddType
      })
    }
  }

  closeAddCustomStep() {
    this._modalOpen = false
    this.addCustomStepModalTarget.classList.add("hidden")
    if (this.hasResponsibleDropdownTarget) this.responsibleDropdownTarget.classList.add("hidden")
    this._unlockScroll()
  }

  toggleResponsibleDropdown(event) {
    event.stopPropagation()
    // Hide already-selected roles from the dropdown
    this.responsibleDropdownTarget.querySelectorAll("button[data-role]").forEach(btn => {
      btn.classList.toggle("hidden", this._selectedResponsibles.has(btn.dataset.role))
    })
    this.responsibleDropdownTarget.classList.toggle("hidden")
  }

  selectResponsible(event) {
    event.stopPropagation()
    const role = event.currentTarget.dataset.role
    const label = event.currentTarget.dataset.label || role
    if (!this._selectedResponsibles) this._selectedResponsibles = new Map()

    if (!this._selectedResponsibles.has(role)) {
      this._selectedResponsibles.set(role, label)
    }
    this.responsibleDropdownTarget.classList.add("hidden")
    this._renderResponsibleChips()
  }

  removeResponsible(event) {
    const role = event.currentTarget.dataset.role
    this._selectedResponsibles.delete(role)
    this._renderResponsibleChips()
  }

  _renderResponsibleChips() {
    if (!this.hasResponsibleChipsTarget) return
    const container = this.responsibleChipsTarget
    container.innerHTML = ""

    if (this._selectedResponsibles && this._selectedResponsibles.size > 0) {
      if (this.hasResponsiblePlaceholderTarget) this.responsiblePlaceholderTarget.textContent = `${this._selectedResponsibles.size} selected`
      this._selectedResponsibles.forEach((label, role) => {
        const chip = document.createElement("span")
        chip.className = "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300"
        chip.innerHTML = `${label}<button type="button" data-role="${role}" data-action="real-estate-steps#removeResponsible" class="ml-1 text-gray-400 hover:text-gray-600 text-sm leading-none font-bold">&times;</button>`
        container.appendChild(chip)
      })
    } else {
      if (this.hasResponsiblePlaceholderTarget) this.responsiblePlaceholderTarget.textContent = "Select roles..."
    }
  }

  // ── Edit modal responsible dropdown ─────────────────────────────────

  toggleEditResponsibleDropdown(event) {
    event.stopPropagation()
    if (!this._editSelectedResponsibles) this._editSelectedResponsibles = new Map()
    this.editResponsibleDropdownTarget.querySelectorAll("button[data-role]").forEach(btn => {
      btn.classList.toggle("hidden", this._editSelectedResponsibles.has(btn.dataset.role))
    })
    this.editResponsibleDropdownTarget.classList.toggle("hidden")
  }

  selectEditResponsible(event) {
    event.stopPropagation()
    const role = event.currentTarget.dataset.role
    const label = event.currentTarget.dataset.label || role
    if (!this._editSelectedResponsibles) this._editSelectedResponsibles = new Map()
    if (!this._editSelectedResponsibles.has(role)) {
      this._editSelectedResponsibles.set(role, label)
    }
    this.editResponsibleDropdownTarget.classList.add("hidden")
    this._renderEditResponsibleChips()
  }

  removeEditResponsible(event) {
    const role = event.currentTarget.dataset.role
    this._editSelectedResponsibles.delete(role)
    this._renderEditResponsibleChips()
  }

  _renderEditResponsibleChips() {
    if (!this.hasEditResponsibleChipsTarget) return
    const container = this.editResponsibleChipsTarget
    container.innerHTML = ""

    if (this._editSelectedResponsibles && this._editSelectedResponsibles.size > 0) {
      if (this.hasEditResponsiblePlaceholderTarget) this.editResponsiblePlaceholderTarget.textContent = `${this._editSelectedResponsibles.size} selected`
      this._editSelectedResponsibles.forEach((label, role) => {
        const chip = document.createElement("span")
        chip.className = "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300"
        chip.innerHTML = `${label}<button type="button" data-role="${role}" data-action="real-estate-steps#removeEditResponsible" class="ml-1 text-gray-400 hover:text-gray-600 text-sm leading-none font-bold">&times;</button>`
        container.appendChild(chip)
      })
    } else {
      if (this.hasEditResponsiblePlaceholderTarget) this.editResponsiblePlaceholderTarget.textContent = "Select roles..."
    }
  }

  _responsibleLabel(role) {
    const labels = {
      REPM: "REPM (Real Estate Project Manager)",
      REA: "REA (Real Estate Analyst)",
      DM: "DM (Discipline Manager)",
      PLM: "PLM (Planning Manager)",
      OP: "OP (Outsource Partner)",
      PM: "PM (Project Manager)",
      FM: "FM (Facilities Manager)",
      RAM: "RAM (Regional Account Manager)",
      SP: "SP (Stake President)",
      NRS: "NRS",
      Team: "Team (SP, PC, REPM, OP, PM, FM, RAM)"
    }
    return labels[role] || role
  }

  // ── Invitee (reminder) dropdown ────────────────────────────────────

  toggleInviteeDropdown(event) {
    event.stopPropagation()
    if (!this._inviteeMembers) {
      this._fetchProjectMembers()
      return
    }
    this._renderInviteeDropdownItems()
    this.inviteeDropdownTarget.classList.toggle("hidden")
  }

  selectInvitee(event) {
    event.stopPropagation()
    const email = event.currentTarget.dataset.email
    const label = event.currentTarget.dataset.label || email
    if (!this._selectedInvitees) this._selectedInvitees = new Map()

    if (!this._selectedInvitees.has(email)) {
      this._selectedInvitees.set(email, label)
    }
    this.inviteeDropdownTarget.classList.add("hidden")
    this._renderInviteeChips()
  }

  removeInvitee(event) {
    const email = event.currentTarget.dataset.email
    this._selectedInvitees.delete(email)
    this._renderInviteeChips()
  }

  _fetchProjectMembers() {
    fetch(`/projects/${this.projectIdValue}/members/fetch_all_members`, {
      headers: { "Accept": "application/json" }
    })
      .then(r => r.json())
      .then(members => {
        this._inviteeMembers = members
        this._renderInviteeDropdownItems()
        this.inviteeDropdownTarget.classList.remove("hidden")
      })
  }

  _renderInviteeDropdownItems() {
    if (!this._inviteeMembers) return
    const dd = this.inviteeDropdownTarget
    dd.innerHTML = ""
    this._inviteeMembers.forEach(m => {
      if (this._selectedInvitees && this._selectedInvitees.has(m.email)) return
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
      btn.dataset.action = "real-estate-steps#selectInvitee"
      btn.dataset.email = m.email
      btn.dataset.label = `${m.name} - ${m.email}`
      btn.textContent = `${m.name} - ${m.email}`
      dd.appendChild(btn)
    })
    if (!dd.children.length) {
      dd.innerHTML = '<p class="px-3 py-2 text-xs text-gray-400">No more members</p>'
    }
  }

  _renderInviteeChips() {
    if (!this.hasInviteeChipsTarget) return
    const container = this.inviteeChipsTarget
    container.innerHTML = ""

    if (this._selectedInvitees && this._selectedInvitees.size > 0) {
      if (this.hasInviteePlaceholderTarget) this.inviteePlaceholderTarget.textContent = `${this._selectedInvitees.size} selected`
      this._selectedInvitees.forEach((label, email) => {
        const chip = document.createElement("span")
        chip.className = "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300"
        chip.innerHTML = `${email}<button type="button" data-email="${email}" data-action="real-estate-steps#removeInvitee" class="ml-1 text-gray-400 hover:text-gray-600 text-sm leading-none font-bold">&times;</button>`
        container.appendChild(chip)
      })
      // Sync hidden input
      if (this.hasReminderInviteListInputTarget) {
        this.reminderInviteListInputTarget.value = Array.from(this._selectedInvitees.keys()).join(" ")
      }
    } else {
      if (this.hasInviteePlaceholderTarget) this.inviteePlaceholderTarget.textContent = "Select members..."
      if (this.hasReminderInviteListInputTarget) this.reminderInviteListInputTarget.value = ""
    }
  }

  submitAddCustomStep(event) {
    event.preventDefault()

    const name = this.customStepNameInputTarget.value.trim()
    if (!name) return

    const responsibles = this._selectedResponsibles
      ? Array.from(this._selectedResponsibles.keys()).join("||")
      : ""

    const data = {
      s: this.stageIdValue,
      step_name: name,
      responsibles: responsibles
    }

    // DD type
    const ddRadio = this.ddTypeRadioTargets.find(r => r.checked)
    if (ddRadio) data.dd_type = ddRadio.value

    this._post(`/real_estate/create_custom_step/${this.projectIdValue}`, data)
      .then(r => r.json())
      .then(step => {
        this.closeAddCustomStep()
        this._insertCustomStepRow(step)
        this._showToast(`Custom step "${step.name}" added successfully`)
      })
  }

  // ── Activity ────────────────────────────────────────────────────────

  updateActivity() {
    const text = this.activityTextareaTarget.value.trim()
    if (!text) return

    const url = this.stageNameValue === "Site Selection"
      ? `/real_estate/${this.projectIdValue}/update_site_selection_status`
      : `/real_estate/update_status/${this.projectIdValue}`

    this._post(url, {
      s: this.stageIdValue,
      status: text
    }).then(r => r.json())
      .then(data => {
        this.activityTextareaTarget.value = ""

        // Parse log text: "DATE - STAGE-ID - REST"
        const logText = data.log_text || ""
        const firstDash = logText.indexOf(" - ")
        const firstPart = firstDash >= 0 ? logText.substring(0, firstDash) : logText
        const rest = firstDash >= 0 ? logText.substring(firstDash + 3) : ""
        const stageName = (data.status || "").split("-")[1] || data.status || ""

        const p = document.createElement("p")
        p.className = "text-[12px] text-gray-400 mb-0.5"
        p.dataset.logId = data.log_id
        p.textContent = `${firstPart} - ${stageName} - ${rest}`

        // Show section if hidden, prepend new log
        if (this.hasHistoryLogSectionTarget) {
          this.historyLogSectionTarget.removeAttribute("hidden")
        }
        if (this.hasHistoryLogTarget) {
          this.historyLogTarget.prepend(p)
        }

        this._showToast("Activity updated")
      })
  }

  // ── History logs ────────────────────────────────────────────────────

  viewAllLogs() {
    this.historyLogsModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeHistoryLogs() {
    this.historyLogsModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  deleteLog(event) {
    this._pendingDeleteLogId = event.currentTarget.dataset.logId
    this._pendingDeleteLogEl = event.currentTarget.closest("div[data-log-id]")
    this.deleteLogConfirmModalTarget.classList.remove("hidden")
  }

  cancelDeleteLog() {
    this._pendingDeleteLogId = null
    this._pendingDeleteLogEl = null
    this.deleteLogConfirmModalTarget.classList.add("hidden")
  }

  confirmDeleteLog() {
    const logId = this._pendingDeleteLogId
    const el = this._pendingDeleteLogEl
    this.deleteLogConfirmModalTarget.classList.add("hidden")

    if (!logId) return

    // Remove from DOM immediately (optimistic)
    if (el) el.remove()
    // Also remove from inline history log
    if (this.hasHistoryLogTarget) {
      const inlineEl = this.historyLogTarget.querySelector(`[data-log-id="${logId}"]`)
      if (inlineEl) inlineEl.remove()
    }

    this._post(`/real_estate/remove_log/${this.projectIdValue}`, { log_id: logId })
    this._showToast("Log entry deleted")

    this._pendingDeleteLogId = null
    this._pendingDeleteLogEl = null
  }

  // ── File upload ─────────────────────────────────────────────────────

  uploadFiles(event) {
    const files = event.currentTarget.files
    if (!files.length) return
    const stepId = this._activeRow?.dataset?.stepId
    this._doUpload(files, (ids) => { this._uploadedAttachmentIds.push(...ids) }, this.fileListTarget, this._uploadedAttachmentNames, this._uploadedAttachmentUrls, stepId)
  }

  triggerFileInput(event) {
    event.stopPropagation()
    this.fileInputTarget.click()
  }

  dragOver(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("border-brand", "bg-blue-50")
    }
  }

  dragLeave(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
  }

  dropFiles(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
    const files = event.dataTransfer.files
    if (!files.length) return
    const stepId = this._activeRow?.dataset?.stepId
    this._doUpload(files, (ids) => { this._uploadedAttachmentIds.push(...ids) }, this.fileListTarget, this._uploadedAttachmentNames, this._uploadedAttachmentUrls, stepId)
  }

  // ── Attachments viewer ──────────────────────────────────────────────

  viewAttachments(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("tr")
    if (!row) return

    const stepName = row.dataset.stepName || "Attachments"
    const names = (row.dataset.attachmentNames || "").split("||").filter(Boolean)
    const urls = (row.dataset.attachmentUrls || "").split("||").filter(Boolean)

    if (this.hasViewAttachmentsTitleTarget) this.viewAttachmentsTitleTarget.textContent = stepName
    if (this.hasViewAttachmentsListTarget) {
      this.viewAttachmentsListTarget.innerHTML = ""
      names.forEach((name, i) => {
        const url = urls[i]
        const chip = document.createElement(url ? "a" : "span")
        if (url) {
          chip.href = url
          chip.target = "_blank"
        }
        chip.className = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 transition-colors cursor-pointer"
        const truncatedName = name.length > 35 ? name.slice(0, 30) + "..." + name.slice(name.lastIndexOf(".")) : name
        chip.innerHTML = `<svg class="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg><span title="${name}">${truncatedName}</span>`
        this.viewAttachmentsListTarget.appendChild(chip)
      })
    }

    if (this.hasViewAttachmentsModalTarget) this.viewAttachmentsModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeViewAttachments() {
    if (this.hasViewAttachmentsModalTarget) this.viewAttachmentsModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  viewAttBackdropMousedown(event) {
    this._viewAttBackdropTarget = event.target
  }

  viewAttCloseOnBackdrop(event) {
    if (event.target === this._viewAttBackdropTarget &&
        this.hasViewAttachmentsDialogTarget &&
        !this.viewAttachmentsDialogTarget.contains(event.target)) {
      this.closeViewAttachments()
    }
  }

  // ── Property modal ─────────────────────────────────────────────────

  openAddProperty(event) {
    this._modalOpen = true
    this._ppAttachmentIds = []
    const type = event.currentTarget.dataset.propertyType || "purchase"

    if (this.hasAddPropertyTitleTarget) this.addPropertyTitleTarget.textContent = `Add ${type === "lease" ? "Lease" : "Purchase"} Property`
    if (this.hasPropertyTypeInputTarget) this.propertyTypeInputTarget.value = type
    if (this.hasPropertyIdInputTarget) this.propertyIdInputTarget.value = ""
    if (this.hasDeletePropertyBtnTarget) this.deletePropertyBtnTarget.classList.add("hidden")

    // Clear form — text inputs
    ;["ppNameInput", "ppAcresInput", "ppRsfInput", "ppAddressInput", "ppCityInput",
      "ppZipcodeInput", "ppPurchasePriceInput",
      "ppLeaseRateInput", "ppCamInput", "ppLeaseTermInput", "ppNotesInput"].forEach(t => {
      if (this[`has${t.charAt(0).toUpperCase() + t.slice(1)}Target`]) {
        this[`${t}Target`].value = ""
      }
    })
    if (this.hasPpStatusInputTarget) this.ppStatusInputTarget.value = "Viable"
    if (this.hasPpCountryInputTarget) this.ppCountryInputTarget.selectedIndex = 0
    if (this.hasPpStateInputTarget) this.ppStateInputTarget.innerHTML = '<option value="">Select...</option>'
    if (this.hasPpFileListTarget) this.ppFileListTarget.innerHTML = ""

    this._togglePropertyFields(type)
    if (this.hasAddPropertyModalTarget) {
      this.addPropertyModalTarget.classList.remove("hidden")
      this._lockScroll()
    }
  }

  async editProperty(event) {
    const row = event.currentTarget.closest("tr")
    if (!row) return
    this.openAddProperty(event)

    const ppId = row.dataset.ppId
    const type = row.dataset.propertyType
    if (this.hasPropertyTypeInputTarget) this.propertyTypeInputTarget.value = type
    if (this.hasPropertyIdInputTarget) this.propertyIdInputTarget.value = ppId
    if (this.hasAddPropertyTitleTarget) this.addPropertyTitleTarget.textContent = `Edit Property`
    if (this.hasDeletePropertyBtnTarget) this.deletePropertyBtnTarget.classList.remove("hidden")

    // Populate all fields from data attributes
    if (this.hasPpNameInputTarget) this.ppNameInputTarget.value = row.dataset.ppName || ""
    if (this.hasPpStatusInputTarget) this.ppStatusInputTarget.value = row.dataset.ppStatus || "Viable"
    if (this.hasPpAcresInputTarget) this.ppAcresInputTarget.value = row.dataset.ppAcres || ""
    if (this.hasPpAddressInputTarget) this.ppAddressInputTarget.value = row.dataset.ppAddress || ""
    if (this.hasPpCityInputTarget) this.ppCityInputTarget.value = row.dataset.ppCity || ""
    if (this.hasPpZipcodeInputTarget) this.ppZipcodeInputTarget.value = row.dataset.ppZipcode || ""
    if (this.hasPpPurchasePriceInputTarget) this.ppPurchasePriceInputTarget.value = row.dataset.ppPurchasePrice || ""
    if (this.hasPpNotesInputTarget) this.ppNotesInputTarget.value = row.dataset.ppNotes || ""
    if (this.hasPpRsfInputTarget) this.ppRsfInputTarget.value = row.dataset.ppRsf || ""
    if (this.hasPpLeaseRateInputTarget) this.ppLeaseRateInputTarget.value = row.dataset.ppLeaseRate || ""
    if (this.hasPpCamInputTarget) this.ppCamInputTarget.value = row.dataset.ppCam || ""
    if (this.hasPpLeaseTermInputTarget) this.ppLeaseTermInputTarget.value = row.dataset.ppLeaseTerm || ""

    // Show existing attachments as removable chips
    const attachmentIds = (row.dataset.attachmentIds || "").split(" ").filter(Boolean)
    const attachmentNames = (row.dataset.attachmentNames || "").split("||").filter(Boolean)
    this._ppAttachmentIds = [...attachmentIds]
    if (this.hasPpFileListTarget) {
      this.ppFileListTarget.innerHTML = ""
      attachmentNames.forEach((name, i) => {
        const attId = attachmentIds[i]
        this.ppFileListTarget.appendChild(this._createFileChip(name, {
          removable: true,
          attachmentId: attId,
          idsArray: this._ppAttachmentIds
        }))
      })
    }

    // Match country by option text and load subregions
    const countryName = row.dataset.ppCountry || ""
    const stateName = row.dataset.ppState || ""
    if (this.hasPpCountryInputTarget && countryName) {
      const opts = this.ppCountryInputTarget.options
      for (let i = 0; i < opts.length; i++) {
        if (opts[i].text === countryName) {
          this.ppCountryInputTarget.selectedIndex = i
          break
        }
      }
      const code = this.ppCountryInputTarget.value
      if (code) await this._loadSubregionsAndSelectState(code, stateName)
    }

    this._togglePropertyFields(type)
  }

  closeAddProperty() {
    this._modalOpen = false
    if (this.hasAddPropertyModalTarget) {
      this.addPropertyModalTarget.classList.add("hidden")
      this._unlockScroll()
    }
  }

  submitProperty(event) {
    event.preventDefault()
    const ppId = this.hasPropertyIdInputTarget ? this.propertyIdInputTarget.value : ""
    const isEdit = ppId !== ""

    const data = {
      s: this.stageIdValue,
      property_type: this.propertyTypeInputTarget.value,
      name: this.ppNameInputTarget.value,
      status: this.ppStatusInputTarget.value,
      acres: this.ppAcresInputTarget.value,
      address: this.ppAddressInputTarget.value,
      city: this.ppCityInputTarget.value,
      state: this.ppStateInputTarget.value,
      country: this.ppCountryInputTarget.value ? this.ppCountryInputTarget.options[this.ppCountryInputTarget.selectedIndex]?.text : "",
      zipcode: this.ppZipcodeInputTarget.value,
      notes: this.ppNotesInputTarget.value,
      attachment_ids: this._ppAttachmentIds.join(" ")
    }

    if (this.propertyTypeInputTarget.value === "purchase") {
      data.purchase_price = this.ppPurchasePriceInputTarget.value
    } else {
      data.rental_square_footage = this.ppRsfInputTarget.value
      data.lease_rate = this.ppLeaseRateInputTarget.value
      data.common_area_maintenance = this.ppCamInputTarget.value
      data.lease_term = this.ppLeaseTermInputTarget.value
    }

    if (isEdit) {
      data.id = ppId
      this._post(`/real_estate/${this.projectIdValue}/update_potential_property`, data)
        .then(r => r.json())
        .then(pp => { this._updatePropertyRow(pp); this.closeAddProperty(); this._showToast("Property updated") })
    } else {
      this._post(`/real_estate/${this.projectIdValue}/create_potential_property`, data)
        .then(r => r.json())
        .then(pp => { this._insertPropertyRow(pp); this.closeAddProperty(); this._showToast("Property added") })
    }
  }

  deleteProperty(event) {
    this._pendingDeletePpId = event.currentTarget.dataset.ppId
    this._pendingDeleteFromModal = false
    if (this.hasDeletePropertyConfirmModalTarget) this.deletePropertyConfirmModalTarget.classList.remove("hidden")
  }

  deletePropertyFromModal() {
    const ppId = this.propertyIdInputTarget.value
    if (!ppId) return
    this._pendingDeletePpId = ppId
    this._pendingDeleteFromModal = true
    if (this.hasDeletePropertyConfirmModalTarget) this.deletePropertyConfirmModalTarget.classList.remove("hidden")
  }

  cancelDeleteProperty() {
    this._pendingDeletePpId = null
    if (this.hasDeletePropertyConfirmModalTarget) this.deletePropertyConfirmModalTarget.classList.add("hidden")
  }

  confirmDeleteProperty() {
    const ppId = this._pendingDeletePpId
    if (!ppId) return
    if (this.hasDeletePropertyConfirmModalTarget) this.deletePropertyConfirmModalTarget.classList.add("hidden")

    fetch(`/real_estate/${this.projectIdValue}/delete_potential_property?id=${ppId}&s=${this.stageIdValue}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Content-Type": "application/json"
      }
    }).then(() => {
      const row = document.querySelector(`tr[data-pp-id="${ppId}"]`)
      const wasPreferred = row && row.className.includes("border-[rgba(40,167,69,0.4)]")
      if (row) row.remove()
      if (this._pendingDeleteFromModal) this.closeAddProperty()
      this._pendingDeletePpId = null
      this._showToast("Property deleted")

      // Remove from "Select Preferred Property" dropdown
      if (this.hasSelectPpPropertySelectTarget) {
        const opt = this.selectPpPropertySelectTarget.querySelector(`option[value="${ppId}"]`)
        if (opt) opt.remove()
      }

      // If the deleted property was the preferred one, clear that section
      if (wasPreferred) {
        this._clearPreferredProperty()
      }

      // Re-number remaining rows and dropdown options
      this._renumberPropertyRows()

      // Hide the Preferred Property section if no properties remain
      const purchaseCount = this.hasPurchaseTableBodyTarget ? this.purchaseTableBodyTarget.querySelectorAll("tr").length : 0
      const leaseCount = this.hasLeaseTableBodyTarget ? this.leaseTableBodyTarget.querySelectorAll("tr").length : 0
      if (purchaseCount + leaseCount === 0) {
        if (this.hasPreferredPropertySectionTarget) this.preferredPropertySectionTarget.hidden = true
        if (this.hasPreferredPropertyPlaceholderTarget) this.preferredPropertyPlaceholderTarget.hidden = false
      }
    })
  }

  uploadPropertyFiles(event) {
    this._uploadFilesGeneric(event, (ids) => { this._ppAttachmentIds.push(...ids) }, this.ppFileListTarget)
  }

  _togglePropertyFields(type) {
    if (this.hasLeaseFieldsTarget) this.leaseFieldsTarget.style.display = type === "lease" ? "" : "none"
    if (this.hasLeaseRateFieldsTarget) this.leaseRateFieldsTarget.style.display = type === "lease" ? "" : "none"
    if (this.hasPurchaseFieldsTarget) this.purchaseFieldsTarget.style.display = type === "purchase" ? "" : "none"
    if (this.hasAcresFieldTarget) this.acresFieldTarget.style.display = type === "purchase" ? "" : "none"
  }

  _updatePropertyRow(pp) {
    const row = document.querySelector(`tr[data-pp-id="${pp.id}"]`)
    if (!row) return

    // Update data attributes
    row.dataset.ppName = pp.name || ""
    row.dataset.ppStatus = pp.status || ""
    row.dataset.ppAcres = pp.acres || ""
    row.dataset.ppAddress = pp.address || ""
    row.dataset.ppCity = pp.city || ""
    row.dataset.ppState = pp.state || ""
    row.dataset.ppCountry = pp.country || ""
    row.dataset.ppZipcode = pp.zipcode || ""
    row.dataset.ppPurchasePrice = pp.purchase_price || ""
    row.dataset.ppNotes = pp.notes || ""
    row.dataset.ppRsf = pp.rental_square_footage || ""
    row.dataset.ppLeaseRate = pp.lease_rate || ""
    row.dataset.ppCam = pp.common_area_maintenance || ""
    row.dataset.ppLeaseTerm = pp.lease_term || ""
    row.dataset.ppOnSiteCosts = pp.on_site_costs || ""
    row.dataset.ppOffSiteCosts = pp.off_site_costs || ""
    row.dataset.ppTenantImprovements = pp.tenant_improvements || ""
    row.dataset.ppDesignAndPermitCosts = pp.design_and_permit_costs || ""
    row.dataset.ppSlab = pp.slab || ""
    row.dataset.ppEarthwork = pp.earthwork || ""
    row.dataset.ppSiteUtilities = pp.site_utilities || ""
    row.dataset.ppAsphalt = pp.asphalt || ""
    row.dataset.ppFencing = pp.fencing || ""
    row.dataset.ppLandscaping = pp.landscaping || ""
    row.dataset.attachmentIds = pp.attachment_ids || ""
    row.dataset.attachmentNames = pp.attachment_names || ""
    row.dataset.attachmentUrls = pp.attachment_urls || ""

    const cells = row.querySelectorAll("td")
    const type = pp.property_type
    const fmt = (v) => {
      const n = parseFloat(v) || 0
      return n === 0 ? "$0" : "$" + Math.round(n).toLocaleString("en-US")
    }

    // cells[0] = #, cells[1] = name, cells[2] = attachments icon, cells[3] = address, ...
    const nameCell = cells[1]
    if (nameCell) { nameCell.textContent = pp.name || ""; nameCell.title = pp.name || "" }

    // Attachment icon — show/hide
    const attCell = cells[2]
    if (attCell) {
      const hasAtts = (pp.attachment_ids || "").trim().length > 0
      if (hasAtts && !attCell.querySelector("a")) {
        attCell.innerHTML = `<a href="#" data-action="click->real-estate-steps#viewPropertyAttachments" class="text-blue-400 hover:text-blue-600"><svg class="w-3.5 h-3.5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" /></svg></a>`
      } else if (!hasAtts) {
        attCell.innerHTML = ""
      }
    }

    cells[3].textContent = pp.address || ""
    cells[4].textContent = pp.city || ""
    cells[5].textContent = `${pp.state || ""} ${pp.zipcode || ""}`.trim()

    if (type === "purchase") {
      cells[6].textContent = pp.acres || ""
      cells[7].textContent = pp.status || ""
      cells[8].textContent = fmt(pp.purchase_price)
      cells[9].textContent = fmt(pp.on_site_costs)
      cells[10].textContent = fmt(pp.off_site_costs)
      const total = (parseFloat(pp.on_site_costs) || 0) + (parseFloat(pp.off_site_costs) || 0) + (parseFloat(pp.purchase_price) || 0)
      cells[11].textContent = fmt(total)
      cells[12].textContent = pp.notes || ""
      cells[12].title = pp.notes || ""
    } else {
      cells[6].textContent = pp.rental_square_footage || ""
      cells[7].textContent = pp.status || ""
      cells[8].textContent = fmt(pp.rental_rate)
      cells[9].textContent = fmt(pp.tenant_improvements)
      cells[10].textContent = fmt(pp.design_and_permit_costs)
      const leaseTotal = (parseFloat(pp.tenant_improvements) || 0) + (parseFloat(pp.design_and_permit_costs) || 0)
      cells[11].textContent = fmt(leaseTotal)
      cells[12].textContent = pp.notes || ""
      cells[12].title = pp.notes || ""
    }

    // Update the dropdown option text
    if (this.hasSelectPpPropertySelectTarget) {
      const opt = this.selectPpPropertySelectTarget.querySelector(`option[value="${pp.id}"]`)
      if (opt) {
        const idx = cells[0] ? cells[0].textContent.trim() : ""
        opt.textContent = `${idx} - ${pp.name || ""}`
      }
    }

    // Update the preferred property row if this property is the preferred one
    this._syncPreferredPropertyRow(row)
  }

  // ── Property country/state cascade ──────────────────────────────────

  async ppCountryChanged() {
    if (!this.hasPpStateInputTarget) return
    this.ppStateInputTarget.innerHTML = '<option value="">Select...</option>'
    const code = this.ppCountryInputTarget.value
    if (!code) return

    try {
      const resp = await fetch(`/locations/subregions?country_code=${code}`, { headers: { Accept: "application/json" } })
      const subregions = await resp.json()
      subregions.forEach(sr => {
        const opt = document.createElement("option")
        opt.value = sr.name
        opt.textContent = sr.name
        this.ppStateInputTarget.appendChild(opt)
      })
    } catch { /* ignore */ }
  }

  async _loadSubregionsAndSelectState(countryCode, stateName) {
    if (!this.hasPpStateInputTarget) return
    this.ppStateInputTarget.innerHTML = '<option value="">Select...</option>'
    if (!countryCode) return

    try {
      const resp = await fetch(`/locations/subregions?country_code=${countryCode}`, { headers: { Accept: "application/json" } })
      const subregions = await resp.json()
      subregions.forEach(sr => {
        const opt = document.createElement("option")
        opt.value = sr.name
        opt.textContent = sr.name
        this.ppStateInputTarget.appendChild(opt)
      })
      if (stateName) this.ppStateInputTarget.value = stateName
    } catch { /* ignore */ }
  }

  // ── Property modal backdrop ─────────────────────────────────────────

  ppBackdropMousedown(event) {
    this._ppBackdropTarget = event.target
  }

  ppCloseOnBackdrop(event) {
    if (event.target === this._ppBackdropTarget &&
        this.hasAddPropertyDialogTarget &&
        !this.addPropertyDialogTarget.contains(event.target)) {
      this.closeAddProperty()
    }
  }

  // ── Property drop zone ──────────────────────────────────────────────

  triggerPpFileInput(event) {
    event.stopPropagation()
    if (this.hasPpFileInputTarget) this.ppFileInputTarget.click()
  }

  ppDragOver(event) {
    event.preventDefault()
    if (this.hasPpDropZoneTarget) {
      this.ppDropZoneTarget.classList.add("border-brand", "bg-blue-50")
    }
  }

  ppDragLeave(event) {
    event.preventDefault()
    if (this.hasPpDropZoneTarget) {
      this.ppDropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
  }

  ppDropFiles(event) {
    event.preventDefault()
    if (this.hasPpDropZoneTarget) {
      this.ppDropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
    const files = event.dataTransfer.files
    if (!files.length) return
    this._doUpload(files, (ids) => { this._ppAttachmentIds.push(...ids) }, this.ppFileListTarget)
  }

  // ── SS Sign Instructions modal ────────────────────────────────────

  closeSsSignInstructions() {
    if (this.hasNeverShowSsCheckboxTarget && this.neverShowSsCheckboxTarget.checked) {
      this._post(`/real_estate/${this.projectIdValue}/ss_sign_instructions_never_show`, { s: this.stageIdValue })
    }
    if (this.hasSsSignInstructionsModalTarget) this.ssSignInstructionsModalTarget.classList.add("hidden")
  }

  // ── Remaining members modal ───────────────────────────────────────

  openRemainingMembers() {
    this._modalOpen = true
    if (this.hasRemainingMembersModalTarget) this.remainingMembersModalTarget.classList.remove("hidden")
  }

  closeRemainingMembers() {
    this._modalOpen = false
    if (this.hasRemainingMembersModalTarget) this.remainingMembersModalTarget.classList.add("hidden")
  }

  submitRemainingMembers(event) {
    event.preventDefault()
    const inputs = this.memberInputTargets
    const data = { s: this.stageIdValue }
    inputs.forEach(input => { if (input.value) data[input.name] = input.value })

    this._post(`/real_estate/${this.projectIdValue}/add_remaining_project_members`, data)
      .then(() => { this._showToast("Members added"); this.closeRemainingMembers() })
  }

  // ── Remind users modal ────────────────────────────────────────────

  remindLeaseUsers() {
    this._modalOpen = true
    if (this.hasRemindUsersModalTarget) this.remindUsersModalTarget.classList.remove("hidden")
  }

  closeRemindUsers() {
    this._modalOpen = false
    if (this.hasRemindUsersModalTarget) this.remindUsersModalTarget.classList.add("hidden")
  }

  toggleRemindSelectAll() {
    const checked = this.remindSelectAllTarget.checked
    this.remindUserCheckboxTargets.forEach(c => c.checked = checked)
    this._updateRemindCount()
  }

  updateRemindCount() { this._updateRemindCount() }

  _updateRemindCount() {
    const count = this.remindUserCheckboxTargets.filter(c => c.checked).length
    const total = this.remindUserCheckboxTargets.length
    if (this.hasRemindSelectedCountTarget) {
      this.remindSelectedCountTarget.textContent = `${count} of ${total} selected`
    }
    if (this.hasRemindSelectAllTarget) {
      this.remindSelectAllTarget.checked = count === total
      this.remindSelectAllTarget.indeterminate = count > 0 && count < total
    }
  }

  submitRemindUsers() {
    const emails = this.remindUserCheckboxTargets
      .filter(c => c.checked)
      .map(c => c.dataset.email)
    const note = this.hasRemindNoteInputTarget ? this.remindNoteInputTarget.value : ""

    this._post(`/real_estate/${this.projectIdValue}/remind_ss_signatures`, {
      s: this.stageIdValue,
      to_email: emails.join(","),
      custom_note: note
    }).then(r => r.json()).then(data => {
      this.closeRemindUsers()
      this._appendLogEntry(data)
      this.lastRemindedTextTargets.forEach(el => {
        el.textContent = `Last reminded on ${data.reminded_date}`
        el.removeAttribute("hidden")
      })
      this._showToast("Reminder sent")
    })
  }

  // ── Request signatures modal ──────────────────────────────────────

  openRequestSigs() {
    this._modalOpen = true
    if (this.hasRequestSigsModalTarget) this.requestSigsModalTarget.classList.remove("hidden")
  }

  closeRequestSigs() {
    this._modalOpen = false
    if (this.hasRequestSigsModalTarget) this.requestSigsModalTarget.classList.add("hidden")
  }

  submitRequestSigs(event) {
    event.preventDefault()
    const form = this.requestSigsFormTarget
    const data = { s: this.stageIdValue }
    new FormData(form).forEach((v, k) => { if (v) data[k] = v })

    this._post(`/real_estate/${this.projectIdValue}/request_ss_signatures`, data)
      .then(r => r.json()).then(json => {
        this.closeRequestSigs()
        this._appendLogEntry(json)
        // Update sig rows and PP row with "Requested on" text
        const typeMap = { "Facilities Manager": "ram", "Stake President": "stake_president", "Real Estate Project Manager": "repm", "Project Manager": "pm" }
        ;(json.sig_requests || []).forEach(req => {
          const signType = typeMap[req.sent_to]
          if (!signType) return
          if (signType === "pm") {
            // Update PP row signature cell
            if (this.hasPpRowTarget) {
              const sigCell = this.ppRowTarget.querySelector('[data-field="signature"]')
              if (sigCell && !sigCell.querySelector('span[style]')) {
                sigCell.innerHTML = `<span class="text-gray-400 italic text-xs">Requested on ${req.date}</span>`
              }
            }
          } else {
            const sigRow = this.sigRowTargets.find(r => r.dataset.signType === signType)
            if (sigRow) {
              const sigCell = sigRow.querySelector('[data-field="signature"]')
              if (sigCell && !sigCell.querySelector('span[style]')) {
                sigCell.innerHTML = `<span class="text-gray-400 italic">Requested on ${req.date}</span>`
              }
            }
          }
        })
        this._showToast("Signature request sent")
      })
  }

  // ── Sign preferred property modal ─────────────────────────────────

  signPreferredProperty(event) {
    const signType = event.currentTarget.dataset.signType

    // Stake President can only sign their own row
    if (this.currentUserIsSpValue && signType !== "stake_president") {
      alert("Stake President can only sign the Stake President row.")
      return
    }

    this._modalOpen = true
    if (this.hasSignPpTypeInputTarget) this.signPpTypeInputTarget.value = signType
    if (this.hasSignPpNotesInputTarget) this.signPpNotesInputTarget.value = ""

    const titles = { ram: "FM (Facilities Manager)", stake_president: "Stake President", repm: "Real Estate Project Manager", cpm: "Construction Project Manager" }
    if (this.hasSignPpTitleTarget) this.signPpTitleTarget.textContent = `${titles[signType] || signType} Signature`

    if (this.hasSignPpModalTarget) this.signPpModalTarget.classList.remove("hidden")
  }

  closeSignPp() {
    this._modalOpen = false
    if (this.hasSignPpModalTarget) this.signPpModalTarget.classList.add("hidden")
  }

  submitSignPp() {
    const signType = this.signPpTypeInputTarget.value
    const notes = this.signPpNotesInputTarget.value
    this._post(`/real_estate/${this.projectIdValue}/sign_preferred_property`, {
      s: this.stageIdValue,
      sign_type: signType,
      notes: notes
    }).then(() => {
      this.closeSignPp()
      this._showToast("Signature submitted")
      this._updateSigRow(signType, {
        signed: true,
        date: new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }),
        name: this.currentUserNameValue,
        notes: notes
      })
    })
  }

  // ── Select preferred property modal ──────────────────────────────

  openSelectPp() {
    this._modalOpen = true
    if (this.hasSelectPpPropertySelectTarget) this.selectPpPropertySelectTarget.value = ""
    if (this.hasSelectPpNotesInputTarget) this.selectPpNotesInputTarget.value = ""
    if (this.hasSelectPpModalTarget) this.selectPpModalTarget.classList.remove("hidden")
  }

  closeSelectPp() {
    this._modalOpen = false
    if (this.hasSelectPpModalTarget) this.selectPpModalTarget.classList.add("hidden")
  }

  submitSelectPp() {
    const ppId = this.selectPpPropertySelectTarget.value
    if (!ppId) { alert("Please select a property."); return }
    const notes = this.selectPpNotesInputTarget.value

    this._post(`/real_estate/${this.projectIdValue}/update_preferred_property`, {
      s: this.stageIdValue,
      potential_property_id: ppId,
      notes: notes
    }).then(r => r.json()).then(data => {
      this.closeSelectPp()
      this._applyPreferredProperty(data)
      this._showToast("Preferred property selected")
    })
  }

  // ── Delete / Change preferred property ────────────────────────────

  deletePreferredProperty(event) {
    event.preventDefault()
    event.currentTarget.checked = true
    this._modalOpen = true
    if (this.hasUnselectPpConfirmModalTarget) this.unselectPpConfirmModalTarget.classList.remove("hidden")
  }

  cancelUnselectPp() {
    this._modalOpen = false
    if (this.hasUnselectPpConfirmModalTarget) this.unselectPpConfirmModalTarget.classList.add("hidden")
  }

  confirmUnselectPp() {
    this.cancelUnselectPp()
    fetch(`/real_estate/${this.projectIdValue}/delete_preferred_property?s=${this.stageIdValue}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Content-Type": "application/json"
      }
    }).then(() => {
      this._clearPreferredProperty()
      this._showToast("Preferred property unselected")
    })
  }

  // ── Unsign preferred property ─────────────────────────────────────

  unsignPreferredProperty(event) {
    event.preventDefault()
    event.currentTarget.checked = true

    const signType = event.currentTarget.dataset.signType
    const signerId = parseInt(event.currentTarget.dataset.signerId)

    // Permission check: signer themselves or REA
    if (signerId !== this.currentUserIdValue && !this.currentUserIsReaValue) {
      if (this.hasOnlySignerCanUnsignModalTarget) this.onlySignerCanUnsignModalTarget.classList.remove("hidden")
      return
    }

    this._pendingUnsignType = signType
    this._modalOpen = true
    if (this.hasUnsignConfirmModalTarget) this.unsignConfirmModalTarget.classList.remove("hidden")
  }

  cancelUnsign() {
    this._modalOpen = false
    if (this.hasUnsignConfirmModalTarget) this.unsignConfirmModalTarget.classList.add("hidden")
  }

  confirmUnsign() {
    const signType = this._pendingUnsignType
    this.cancelUnsign()
    fetch(`/real_estate/${this.projectIdValue}/delete_preferred_property_signature?s=${this.stageIdValue}&sign_type=${signType}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Content-Type": "application/json"
      }
    }).then(() => {
      this._updateSigRow(signType, { signed: false })
      this._showToast("Signature removed")
    })
  }

  // ── Validation modals ────────────────────────────────────────────

  closePmMustSelectModal() {
    if (this.hasPmMustSelectModalTarget) this.pmMustSelectModalTarget.classList.add("hidden")
  }

  closeOnlySignerCanUnsignModal() {
    if (this.hasOnlySignerCanUnsignModalTarget) this.onlySignerCanUnsignModalTarget.classList.add("hidden")
  }

  // ── Edit cost modal ───────────────────────────────────────────────

  openEditCost(event) {
    event.stopPropagation()
    this._modalOpen = true
    this._costAttachmentIds = []
    document.body.classList.add("overflow-hidden")

    const costType = event.currentTarget.dataset.costType
    const ppId = event.currentTarget.dataset.ppId
    const row = event.currentTarget.closest("tr")

    if (this.hasEditCostTypeInputTarget) this.editCostTypeInputTarget.value = costType
    if (this.hasEditCostPpIdInputTarget) this.editCostPpIdInputTarget.value = ppId
    if (this.hasEditCostFileListTarget) this.editCostFileListTarget.innerHTML = ""

    // Title
    const titles = {
      on_site: "On-Site Costs",
      off_site: "Off-Site Costs",
      rental_rate: "Asking Rental Rate",
      tenant_improvements: "Tenant Improvements",
      design_and_permit: "Design & Permit Costs"
    }
    if (this.hasEditCostTitleTarget) this.editCostTitleTarget.textContent = titles[costType] || "Edit Cost"

    // Hide all sub-field sections
    if (this.hasOnSiteSubFieldsTarget) this.onSiteSubFieldsTarget.classList.add("hidden")
    if (this.hasRentalRateSubFieldsTarget) this.rentalRateSubFieldsTarget.classList.add("hidden")
    if (this.hasEditCostAmountFieldTarget) this.editCostAmountFieldTarget.classList.add("hidden")
    if (this.hasTiAmountFieldTarget) this.tiAmountFieldTarget.classList.add("hidden")
    if (this.hasDpAmountFieldTarget) this.dpAmountFieldTarget.classList.add("hidden")

    // Show relevant fields and populate values from row data
    if (costType === "on_site") {
      if (this.hasOnSiteSubFieldsTarget) this.onSiteSubFieldsTarget.classList.remove("hidden")
      if (this.hasEditCostSlabInputTarget) this.editCostSlabInputTarget.value = row.dataset.ppSlab || ""
      if (this.hasEditCostEarthworkInputTarget) this.editCostEarthworkInputTarget.value = row.dataset.ppEarthwork || ""
      if (this.hasEditCostSiteUtilitiesInputTarget) this.editCostSiteUtilitiesInputTarget.value = row.dataset.ppSiteUtilities || ""
      if (this.hasEditCostAsphaltInputTarget) this.editCostAsphaltInputTarget.value = row.dataset.ppAsphalt || ""
      if (this.hasEditCostFencingInputTarget) this.editCostFencingInputTarget.value = row.dataset.ppFencing || ""
      if (this.hasEditCostLandscapingInputTarget) this.editCostLandscapingInputTarget.value = row.dataset.ppLandscaping || ""
      this.recalcOnSiteTotal()
    } else if (costType === "off_site") {
      if (this.hasEditCostAmountFieldTarget) this.editCostAmountFieldTarget.classList.remove("hidden")
      if (this.hasEditCostAmountInputTarget) this.editCostAmountInputTarget.value = row.dataset.ppOffSiteCosts || ""
    } else if (costType === "rental_rate") {
      if (this.hasRentalRateSubFieldsTarget) this.rentalRateSubFieldsTarget.classList.remove("hidden")
      if (this.hasEditCostLeaseRateInputTarget) this.editCostLeaseRateInputTarget.value = row.dataset.ppLeaseRate || ""
      if (this.hasEditCostRsfInputTarget) this.editCostRsfInputTarget.value = row.dataset.ppRsf || ""
      if (this.hasEditCostLeaseTermInputTarget) this.editCostLeaseTermInputTarget.value = row.dataset.ppLeaseTerm || ""
      if (this.hasEditCostCamInputTarget) this.editCostCamInputTarget.value = row.dataset.ppCam || ""
      this.recalcRentalRateTotal()
    } else if (costType === "tenant_improvements") {
      if (this.hasTiAmountFieldTarget) this.tiAmountFieldTarget.classList.remove("hidden")
      if (this.hasEditCostTiInputTarget) this.editCostTiInputTarget.value = row.dataset.ppTenantImprovements || ""
    } else if (costType === "design_and_permit") {
      if (this.hasDpAmountFieldTarget) this.dpAmountFieldTarget.classList.remove("hidden")
      if (this.hasEditCostDpInputTarget) this.editCostDpInputTarget.value = row.dataset.ppDesignAndPermitCosts || ""
    }

    if (this.hasEditCostModalTarget) this.editCostModalTarget.classList.remove("hidden")

    // Fetch and display existing cost attachments
    this._loadCostAttachments(ppId, costType)
  }

  async _loadCostAttachments(ppId, costType) {
    try {
      const res = await fetch(`/real_estate/${this.projectIdValue}/pp_cost_attachments?pp_id=${ppId}&cost_type=${costType}`)
      if (!res.ok) return
      const attachments = await res.json()
      if (!attachments.length) return

      this._costAttachmentIds = attachments.map(a => String(a.id))
      if (this.hasEditCostFileListTarget) {
        attachments.forEach(a => {
          const chip = this._createFileChip(a.name, {
            removable: true,
            attachmentId: String(a.id),
            idsArray: this._costAttachmentIds,
            url: a.url
          })
          this.editCostFileListTarget.appendChild(chip)
        })
      }
    } catch (e) {
      console.error("Failed to load cost attachments:", e)
    }
  }

  closeEditCost() {
    this._modalOpen = false
    document.body.classList.remove("overflow-hidden")
    if (this.hasEditCostModalTarget) this.editCostModalTarget.classList.add("hidden")
  }

  submitEditCost() {
    const costType = this.editCostTypeInputTarget.value
    const ppId = this.editCostPpIdInputTarget.value
    const base = { s: this.stageIdValue, pp_id: ppId, attachment_ids: (this._costAttachmentIds || []).join(" ") }
    const fmt = (v) => {
      const n = parseFloat(v) || 0
      return n === 0 ? "$0" : "$" + Math.round(n).toLocaleString("en-US")
    }

    let url, data
    if (costType === "on_site") {
      const amount = this._onSiteTotal()
      url = `/real_estate/${this.projectIdValue}/update_pp_on_site_costs`
      data = {
        ...base,
        amount,
        slab: this.hasEditCostSlabInputTarget ? this.editCostSlabInputTarget.value : "",
        earthwork: this.hasEditCostEarthworkInputTarget ? this.editCostEarthworkInputTarget.value : "",
        site_utilities: this.hasEditCostSiteUtilitiesInputTarget ? this.editCostSiteUtilitiesInputTarget.value : "",
        asphalt: this.hasEditCostAsphaltInputTarget ? this.editCostAsphaltInputTarget.value : "",
        fencing: this.hasEditCostFencingInputTarget ? this.editCostFencingInputTarget.value : "",
        landscaping: this.hasEditCostLandscapingInputTarget ? this.editCostLandscapingInputTarget.value : ""
      }
    } else if (costType === "off_site") {
      url = `/real_estate/${this.projectIdValue}/update_pp_off_site_costs`
      data = { ...base, amount: this.editCostAmountInputTarget.value }
    } else if (costType === "rental_rate") {
      url = `/real_estate/${this.projectIdValue}/update_pp_rental_rate`
      data = {
        ...base,
        lease_rate: this.hasEditCostLeaseRateInputTarget ? this.editCostLeaseRateInputTarget.value : "",
        rental_square_footage: this.hasEditCostRsfInputTarget ? this.editCostRsfInputTarget.value : "",
        lease_term: this.hasEditCostLeaseTermInputTarget ? this.editCostLeaseTermInputTarget.value : "",
        common_area_maintenance: this.hasEditCostCamInputTarget ? this.editCostCamInputTarget.value : "",
        rental_rate: this._rentalRateTotal()
      }
    } else if (costType === "tenant_improvements") {
      url = `/real_estate/${this.projectIdValue}/update_pp_tenant_improvements`
      data = { ...base, tenant_improvements: this.editCostTiInputTarget.value }
    } else if (costType === "design_and_permit") {
      url = `/real_estate/${this.projectIdValue}/update_pp_design_and_permit_costs`
      data = { ...base, design_and_permit_costs: this.editCostDpInputTarget.value }
    }

    this._post(url, data).then(() => {
      // Update row cells inline instead of reloading
      const row = document.querySelector(`tr[data-pp-id="${ppId}"]`)
      if (row) {
        const cells = row.querySelectorAll("td")
        const propertyType = row.dataset.propertyType

        if (costType === "on_site") {
          const total = this._onSiteTotal()
          row.dataset.ppOnSiteCosts = total
          row.dataset.ppSlab = data.slab
          row.dataset.ppEarthwork = data.earthwork
          row.dataset.ppSiteUtilities = data.site_utilities
          row.dataset.ppAsphalt = data.asphalt
          row.dataset.ppFencing = data.fencing
          row.dataset.ppLandscaping = data.landscaping
          cells[9].textContent = fmt(total)
          const grandTotal = total + (parseFloat(row.dataset.ppOffSiteCosts) || 0) + (parseFloat(row.dataset.ppPurchasePrice) || 0)
          cells[11].textContent = fmt(grandTotal)
        } else if (costType === "off_site") {
          const amount = parseFloat(data.amount) || 0
          row.dataset.ppOffSiteCosts = amount
          cells[10].textContent = fmt(amount)
          const grandTotal = (parseFloat(row.dataset.ppOnSiteCosts) || 0) + amount + (parseFloat(row.dataset.ppPurchasePrice) || 0)
          cells[11].textContent = fmt(grandTotal)
        } else if (costType === "rental_rate") {
          row.dataset.ppLeaseRate = data.lease_rate
          row.dataset.ppRsf = data.rental_square_footage
          row.dataset.ppLeaseTerm = data.lease_term
          row.dataset.ppCam = data.common_area_maintenance
          row.dataset.ppRentalRate = data.rental_rate
          cells[8].textContent = fmt(data.rental_rate)
          const leaseTotal = (parseFloat(data.rental_rate) || 0) + (parseFloat(row.dataset.ppTenantImprovements) || 0) + (parseFloat(row.dataset.ppDesignAndPermitCosts) || 0)
          cells[11].textContent = fmt(leaseTotal)
        } else if (costType === "tenant_improvements") {
          const amount = parseFloat(data.tenant_improvements) || 0
          row.dataset.ppTenantImprovements = amount
          cells[9].textContent = fmt(amount)
          const leaseTotal = (parseFloat(row.dataset.ppRentalRate) || 0) + amount + (parseFloat(row.dataset.ppDesignAndPermitCosts) || 0)
          cells[11].textContent = fmt(leaseTotal)
        } else if (costType === "design_and_permit") {
          const amount = parseFloat(data.design_and_permit_costs) || 0
          row.dataset.ppDesignAndPermitCosts = amount
          cells[10].textContent = fmt(amount)
          const leaseTotal = (parseFloat(row.dataset.ppRentalRate) || 0) + (parseFloat(row.dataset.ppTenantImprovements) || 0) + amount
          cells[11].textContent = fmt(leaseTotal)
        }
      }

      // Update the preferred property row text if this property is the preferred one
      this._syncPreferredPropertyRow(row)
      this.closeEditCost()
      this._showToast("Costs updated")
    })
  }

  uploadCostFiles(event) {
    this._costAttachmentIds = this._costAttachmentIds || []
    this._uploadFilesGeneric(event, (ids) => { this._costAttachmentIds.push(...ids) }, this.editCostFileListTarget)
  }

  // ── Cost modal: real-time total & drag-drop ─────────────────────

  recalcOnSiteTotal() {
    const total = this._onSiteTotal()
    if (this.hasOnSiteTotalDisplayTarget) {
      this.onSiteTotalDisplayTarget.textContent = total === 0
        ? "$0.00"
        : "$" + total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }

  _onSiteTotal() {
    const v = (target) => parseFloat(target?.value) || 0
    return v(this.hasEditCostSlabInputTarget && this.editCostSlabInputTarget)
         + v(this.hasEditCostEarthworkInputTarget && this.editCostEarthworkInputTarget)
         + v(this.hasEditCostSiteUtilitiesInputTarget && this.editCostSiteUtilitiesInputTarget)
         + v(this.hasEditCostAsphaltInputTarget && this.editCostAsphaltInputTarget)
         + v(this.hasEditCostFencingInputTarget && this.editCostFencingInputTarget)
         + v(this.hasEditCostLandscapingInputTarget && this.editCostLandscapingInputTarget)
  }

  recalcRentalRateTotal() {
    const total = this._rentalRateTotal()
    if (this.hasRentalRateTotalDisplayTarget) {
      this.rentalRateTotalDisplayTarget.textContent = total === 0
        ? "$0.00"
        : "$" + total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }

  _rentalRateTotal() {
    const v = (target) => parseFloat(target?.value) || 0
    const leaseRate = v(this.hasEditCostLeaseRateInputTarget && this.editCostLeaseRateInputTarget)
    const cam = v(this.hasEditCostCamInputTarget && this.editCostCamInputTarget)
    const rsf = v(this.hasEditCostRsfInputTarget && this.editCostRsfInputTarget)
    const leaseTerm = v(this.hasEditCostLeaseTermInputTarget && this.editCostLeaseTermInputTarget)
    return (leaseRate + cam) * rsf * leaseTerm
  }

  triggerCostFileInput(event) {
    event.stopPropagation()
    if (this.hasEditCostFileInputTarget) this.editCostFileInputTarget.click()
  }

  costDragOver(event) {
    event.preventDefault()
    if (this.hasEditCostDropZoneTarget) {
      this.editCostDropZoneTarget.classList.add("border-brand", "bg-blue-50")
    }
  }

  costDragLeave(event) {
    event.preventDefault()
    if (this.hasEditCostDropZoneTarget) {
      this.editCostDropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
  }

  costDropFiles(event) {
    event.preventDefault()
    if (this.hasEditCostDropZoneTarget) {
      this.editCostDropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
    const files = event.dataTransfer.files
    if (!files.length) return
    this._costAttachmentIds = this._costAttachmentIds || []
    this._doUpload(files, (ids) => { this._costAttachmentIds.push(...ids) }, this.editCostFileListTarget)
  }

  // ── Property attachments viewer ───────────────────────────────────

  viewPropertyAttachments(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("tr")
    if (!row) return

    const ppName = row.dataset.ppName || "Attachments"
    const names = (row.dataset.attachmentNames || "").split("||").filter(Boolean)
    const urls = (row.dataset.attachmentUrls || "").split("||").filter(Boolean)

    if (this.hasViewAttachmentsTitleTarget) this.viewAttachmentsTitleTarget.textContent = ppName
    if (this.hasViewAttachmentsListTarget) {
      this.viewAttachmentsListTarget.innerHTML = ""
      names.forEach((name, i) => {
        const url = urls[i]
        const chip = document.createElement(url ? "a" : "span")
        if (url) {
          chip.href = url
          chip.target = "_blank"
        }
        chip.className = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 transition-colors cursor-pointer"
        const truncatedName = name.length > 35 ? name.slice(0, 30) + "..." + name.slice(name.lastIndexOf(".")) : name
        chip.innerHTML = `<svg class="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg><span title="${name}">${truncatedName}</span>`
        this.viewAttachmentsListTarget.appendChild(chip)
      })
    }

    if (this.hasViewAttachmentsModalTarget) this.viewAttachmentsModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  // ── FCW modal ─────────────────────────────────────────────────────

  openFcw() {
    this._modalOpen = true
    this._fcwAttachmentIds = []

    // Load existing attachments from the FCW step row
    const row = this._findStepRow("Feasibility Cost Worksheet")
    if (row) {
      const existingIds = (row.dataset.attachmentIds || "").split(" ").filter(Boolean)
      const existingNames = (row.dataset.attachmentNames || "").split("||").filter(Boolean)
      this._fcwAttachmentIds.push(...existingIds)

      if (this.hasFcwFileListTarget) {
        this.fcwFileListTarget.innerHTML = ""
        existingNames.forEach((name, i) => {
          const attId = existingIds[i]
          this.fcwFileListTarget.appendChild(this._createFileChip(name, {
            removable: true,
            attachmentId: attId,
            idsArray: this._fcwAttachmentIds
          }))
        })
      }
    } else if (this.hasFcwFileListTarget) {
      this.fcwFileListTarget.innerHTML = ""
    }

    if (this.hasFcwModalTarget) this.fcwModalTarget.classList.remove("hidden")
    this._lockScroll()
    this.recalcFcwCosts()
  }

  closeFcw() {
    this._modalOpen = false
    if (this.hasFcwModalTarget) this.fcwModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  saveFcwDraft() {
    const data = this._collectFcwData()
    this._post(`/real_estate/${this.projectIdValue}/save_fcw_draft`, {
      s: this.stageIdValue,
      data: JSON.stringify(data),
      attachment_ids: this._fcwAttachmentIds.join(" ")
    }).then(r => r.json()).then(json => {
      const row = this._findStepRow("Feasibility Cost Worksheet")
      if (row) this._syncRowAttachments(row, json.attachments)
      this.closeFcw()
      this._showToast("FCW draft saved")
    })
  }

  completeFcw() {
    const data = this._collectFcwData()
    this._post(`/real_estate/${this.projectIdValue}/complete_fcw`, {
      s: this.stageIdValue,
      data: JSON.stringify(data),
      attachment_ids: this._fcwAttachmentIds.join(" ")
    }).then(r => r.json()).then(json => {
      const row = this._findStepRow("Feasibility Cost Worksheet")
      if (row) {
        this._syncRowAttachments(row, json.attachments)
        const today = new Date().toISOString().split("T")[0]
        this._updateRowCells(row, { completed: true, date: today, userName: this.currentUserNameValue })
      }
      this.closeFcw()
      this._showToast("FCW completed")
    })
  }

  uploadFcwFiles(event) {
    this._uploadFilesGeneric(event, (ids) => { this._fcwAttachmentIds.push(...ids) }, this.fcwFileListTarget)
  }

  triggerFcwFileInput(event) {
    event.stopPropagation()
    if (this.hasFcwFileInputTarget) this.fcwFileInputTarget.click()
  }

  fcwDragOver(event) {
    event.preventDefault()
    if (this.hasFcwDropZoneTarget) {
      this.fcwDropZoneTarget.classList.add("border-brand", "bg-blue-50")
    }
  }

  fcwDragLeave(event) {
    event.preventDefault()
    if (this.hasFcwDropZoneTarget) {
      this.fcwDropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
  }

  fcwDropFiles(event) {
    event.preventDefault()
    if (this.hasFcwDropZoneTarget) {
      this.fcwDropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
    const files = event.dataTransfer.files
    if (!files.length) return
    this._doUpload(files, (ids) => { this._fcwAttachmentIds.push(...ids) }, this.fcwFileListTarget)
  }

  recalcFcwCosts() {
    const modal = this.fcwModalTarget
    const val = (field) => {
      const input = modal.querySelector(`[data-fcw-field="${field}"]`)
      return parseFloat((input?.value || "").replace(/[^0-9.-]/g, "")) || 0
    }
    const fmt = (n) => n === 0 ? "" : n.toLocaleString("en-US", { style: "currency", currency: "USD" })
    const sqft = val("sqft")
    const perSqft = (amount) => (sqft > 0 && amount !== 0) ? fmt(amount / sqft) : ""

    const slabFields = ["foundation", "earthwork", "utilities", "asphalt", "fencing", "landscaping"]
    const slabDown = slabFields.reduce((sum, f) => sum + val(f), 0)
    const totalOffsite = val("total_offsite")
    const purchasePrice = val("purchase_price")
    const offsitePurchase = totalOffsite + purchasePrice
    const totalCost = slabDown + offsitePurchase
    const unusual = val("unusual")

    // Update $/Square Foot for each editable cost row
    ;[...slabFields, "total_offsite", "purchase_price", "unusual"].forEach(f => {
      const cell = modal.querySelector(`[data-fcw-sqft="${f}"]`)
      if (cell) cell.textContent = perSqft(val(f))
    })

    // Update computed rows
    const calcs = {
      slab_down_cost: fmt(slabDown), slab_down_sqft: perSqft(slabDown),
      offsite_purchase_cost: fmt(offsitePurchase), offsite_purchase_sqft: perSqft(offsitePurchase),
      total_cost: fmt(totalCost), total_sqft: perSqft(totalCost)
    }
    Object.entries(calcs).forEach(([key, value]) => {
      const cell = modal.querySelector(`[data-fcw-calc="${key}"]`)
      if (cell) cell.textContent = value
    })
  }

  _collectFcwData() {
    const rows = {}
    this.fcwResponseTargets.forEach((sel, idx) => {
      const comment = this.fcwCommentTargets[idx]?.value || ""
      rows[idx] = [sel.value, comment]
    })
    const costs = {}
    this.fcwCostInputTargets.forEach(input => {
      const raw = (input.value || "").replace(/[^0-9.-]/g, "")
      costs[input.dataset.fcwField] = raw ? parseFloat(raw) : ""
    })
    return {
      rows,
      status: this.hasFcwStatusInputTarget ? this.fcwStatusInputTarget.value : "",
      ...costs
    }
  }

  // ── NRS modal ─────────────────────────────────────────────────────

  openNrs() {
    this._modalOpen = true
    this._nrsAttachmentIds = []

    // Load existing attachments from the NRS Review row
    const row = this._findStepRow("NRS Review")
    if (row) {
      const existingIds = (row.dataset.attachmentIds || "").split(" ").filter(Boolean)
      const existingNames = (row.dataset.attachmentNames || "").split("||").filter(Boolean)
      this._nrsAttachmentIds.push(...existingIds)

      if (this.hasNrsFileListTarget) {
        this.nrsFileListTarget.innerHTML = ""
        existingNames.forEach((name, i) => {
          const attId = existingIds[i]
          this.nrsFileListTarget.appendChild(this._createFileChip(name, {
            removable: true,
            attachmentId: attId,
            idsArray: this._nrsAttachmentIds
          }))
        })
      }
    } else if (this.hasNrsFileListTarget) {
      this.nrsFileListTarget.innerHTML = ""
    }

    if (this.hasNrsModalTarget) this.nrsModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeNrs() {
    this._modalOpen = false
    if (this.hasNrsModalTarget) this.nrsModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  // ── NRS drop zone ──────────────────────────────────────────────────

  triggerNrsFileInput(event) {
    event.stopPropagation()
    if (this.hasNrsFileInputTarget) this.nrsFileInputTarget.click()
  }

  nrsDragOver(event) {
    event.preventDefault()
    if (this.hasNrsDropZoneTarget) {
      this.nrsDropZoneTarget.classList.add("border-brand", "bg-blue-50")
    }
  }

  nrsDragLeave(event) {
    event.preventDefault()
    if (this.hasNrsDropZoneTarget) {
      this.nrsDropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
  }

  nrsDropFiles(event) {
    event.preventDefault()
    if (this.hasNrsDropZoneTarget) {
      this.nrsDropZoneTarget.classList.remove("border-brand", "bg-blue-50")
    }
    const files = event.dataTransfer.files
    if (!files.length) return
    this._doUpload(files, (ids) => { this._nrsAttachmentIds.push(...ids) }, this.nrsFileListTarget)
  }

  saveNrsDraft() {
    const data = this._collectNrsData()
    this._post(`/real_estate/${this.projectIdValue}/save_nrs_draft`, {
      s: this.stageIdValue,
      data: JSON.stringify(data),
      attachment_ids: this._nrsAttachmentIds.join(" ")
    }).then(r => r.json()).then(json => {
      const row = this._findStepRow("NRS Review")
      if (row) this._syncRowAttachments(row, json.attachments)
      this.closeNrs()
      this._showToast("NRS Review draft saved")
    })
  }

  completeNrs() {
    const data = this._collectNrsData()
    this._post(`/real_estate/${this.projectIdValue}/complete_nrs`, {
      s: this.stageIdValue,
      data: JSON.stringify(data),
      attachment_ids: this._nrsAttachmentIds.join(" ")
    }).then(r => r.json()).then(json => {
      const row = this._findStepRow("NRS Review")
      if (row) {
        const today = new Date().toISOString().slice(0, 10)
        this._updateRowCells(row, { completed: true, date: today, userName: this.currentUserNameValue, notes: data.status })
        this._syncRowAttachments(row, json.attachments)
      }
      this.closeNrs()
      this._showToast("NRS Review completed")
    })
  }

  _findStepRow(name) {
    return this.stepRowTargets.find(r => r.dataset.stepName === name) || null
  }

  uploadNrsFiles(event) {
    this._uploadFilesGeneric(event, (ids) => { this._nrsAttachmentIds.push(...ids) }, this.nrsFileListTarget)
  }

  // ── NRS dynamic rows (water/company/district) ──────────────────────

  addNrsWaterRow(event) {
    const tableType = event.currentTarget.dataset.table
    const targetMap = { water: "nrsWaterTable", company: "nrsCompanyTable", district: "nrsDistrictTable" }
    const table = this[`${targetMap[tableType]}Target`]
    if (!table) return

    const tr = document.createElement("tr")
    tr.className = "border-b border-gray-200"

    if (tableType === "district") {
      tr.innerHTML = `
        <td class="px-1 py-0.5 border-r border-gray-200 bg-amber-50"><input type="text" class="nrs-dyn-cell w-full px-2 py-1 bg-transparent border-0 focus:outline-none focus:ring-0"></td>
        <td class="px-1 py-0.5 border-r border-gray-200 bg-amber-50"><input type="text" class="nrs-dyn-cell w-full px-2 py-1 bg-transparent border-0 focus:outline-none focus:ring-0"></td>
        <td class="px-1 py-0.5 bg-amber-50 relative">
          <select class="nrs-dyn-cell w-full px-2 py-1 bg-transparent border-0 focus:outline-none focus:ring-0">
            <option value=""></option><option value="Yes">Yes</option><option value="No">No</option>
          </select>
          <button type="button" class="absolute right-1 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600"
                  data-action="real-estate-steps#removeNrsDynRow">\u2715</button>
        </td>`
    } else {
      tr.innerHTML = `
        <td class="px-1 py-0.5 border-r border-gray-200 bg-amber-50"><input type="text" class="nrs-dyn-cell w-full px-2 py-1 bg-transparent border-0 focus:outline-none focus:ring-0"></td>
        <td class="px-1 py-0.5 border-r border-gray-200 bg-amber-50"><input type="text" class="nrs-dyn-cell w-full px-2 py-1 bg-transparent border-0 focus:outline-none focus:ring-0"></td>
        <td class="px-1 py-0.5 bg-amber-50 relative"><input type="text" class="nrs-dyn-cell w-full px-2 py-1 pr-6 bg-transparent border-0 focus:outline-none focus:ring-0">
          <button type="button" class="absolute right-1 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600"
                  data-action="real-estate-steps#removeNrsDynRow">\u2715</button>
        </td>`
    }

    table.querySelector("tbody").appendChild(tr)
  }

  removeNrsDynRow(event) {
    const tr = event.currentTarget.closest("tr")
    const tbody = tr.closest("tbody")
    if (tbody.querySelectorAll("tr").length > 1) {
      tr.remove()
    } else {
      tr.querySelectorAll(".nrs-dyn-cell").forEach(c => { c.value = "" })
    }
  }

  // ── NRS signatures ────────────────────────────────────────────────

  promptNrsSig(event) {
    const tr = event.currentTarget.closest("tr")
    const roleName = tr.querySelectorAll("td")[0].textContent.trim()
    this._pendingSigRow = tr
    this.nrsSigConfirmTitleTarget.textContent = `Sign — ${roleName}`
    this.nrsSigConfirmModalTarget.classList.remove("hidden")
  }

  cancelNrsSig() {
    this._pendingSigRow = null
    this.nrsSigConfirmModalTarget.classList.add("hidden")
  }

  confirmNrsSig() {
    const tr = this._pendingSigRow
    if (!tr) return

    const sigTd = tr.querySelectorAll("td")[1]
    const dateTd = tr.querySelectorAll("td")[2]
    const today = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })

    sigTd.innerHTML = `
      <span class="text-gray-700 text-lg" style="font-family: 'Allura', cursive;" data-real-estate-steps-target="nrsSigName">${this.currentUserNameValue}</span>
      <button type="button" class="ml-2 text-red-400 hover:text-red-600 text-xs"
              data-action="real-estate-steps#removeNrsSig">remove</button>`
    dateTd.textContent = today

    this._pendingSigRow = null
    this.nrsSigConfirmModalTarget.classList.add("hidden")
  }

  removeNrsSig(event) {
    const tr = event.currentTarget.closest("tr")
    const sigTd = tr.querySelectorAll("td")[1]
    const dateTd = tr.querySelectorAll("td")[2]

    sigTd.innerHTML = `
      <button type="button" class="text-brand text-xs font-semibold hover:underline"
              data-action="real-estate-steps#promptNrsSig">SIGN HERE</button>`
    dateTd.textContent = ""
  }

  toggleResponsePopup(event) {
    event.stopPropagation()
    const td = event.currentTarget

    // If clicking the same cell that's already open, just close
    const wasOpen = this._currentResponseTd === td
    this._closeResponsePopup()
    if (wasOpen) return

    const input = td.querySelector('input[type="hidden"]')
    const display = td.querySelector('[data-response-display]')

    const popup = document.createElement('div')
    popup.className = 'absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[80px]'

    const options = [['Yes', 'Yes'], ['No', 'No'], ['', 'Clear']]
    options.forEach(([val, label]) => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'block w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100'
      btn.textContent = label
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        input.value = val
        display.textContent = val
        this._closeResponsePopup()
        // Toggle water section when Q5 changes
        if (input.name === "nrs_question_5" && this.hasNrsWaterSectionTarget) {
          this.nrsWaterSectionTarget.classList.toggle("hidden", val !== "Yes")
        }
      })
      popup.appendChild(btn)
    })

    td.appendChild(popup)
    this._currentResponsePopup = popup
    this._currentResponseTd = td

    // Use capture phase so clicks inside the stopPropagation modal still reach us
    requestAnimationFrame(() => {
      this._closeResponsePopupHandler = (e) => {
        if (this._currentResponsePopup && !this._currentResponsePopup.contains(e.target)) {
          e.stopImmediatePropagation()
          this._closeResponsePopup()
        }
      }
      document.addEventListener('click', this._closeResponsePopupHandler, true)
    })
  }

  _closeResponsePopup() {
    if (this._currentResponsePopup) {
      this._currentResponsePopup.remove()
      this._currentResponsePopup = null
      this._currentResponseTd = null
    }
    if (this._closeResponsePopupHandler) {
      document.removeEventListener('click', this._closeResponsePopupHandler, true)
      this._closeResponsePopupHandler = null
    }
  }

  _collectNrsData() {
    const data = {
      date: this.hasNrsDateInputTarget ? this.nrsDateInputTarget.value : "",
      property_use: this.hasNrsPropertyUseInputTarget ? this.nrsPropertyUseInputTarget.value : "",
      transaction_acres: this.hasNrsAcresInputTarget ? this.nrsAcresInputTarget.value : "",
      property_title: this.hasNrsTitleInputTarget ? this.nrsTitleInputTarget.value : "",
      property_number: this.hasNrsPropertyNumberInputTarget ? this.nrsPropertyNumberInputTarget.value : "",
      status: this.hasNrsStatusInputTarget ? this.nrsStatusInputTarget.value : ""
    }

    // Questions
    this.nrsResponseTargets.forEach((sel, idx) => {
      const qNum = sel.name.replace("nrs_question_", "")
      data[`question_${qNum}`] = sel.value
      const commentInput = this.nrsCommentTargets[idx]
      if (commentInput) data[`question_${qNum}_comment`] = commentInput.value
    })

    // Dynamic water/company/district rows
    data.water_rows = this._collectNrsTableRows(this.hasNrsWaterTableTarget ? this.nrsWaterTableTarget : null)
    data.company_rows = this._collectNrsTableRows(this.hasNrsCompanyTableTarget ? this.nrsCompanyTableTarget : null)
    data.district_rows = this._collectNrsTableRows(this.hasNrsDistrictTableTarget ? this.nrsDistrictTableTarget : null)

    // Signatures
    const modal = this.nrsModalTarget
    modal.querySelectorAll("[data-sig-key]").forEach(tr => {
      const key = tr.dataset.sigKey
      const nameEl = tr.querySelector("[data-real-estate-steps-target='nrsSigName']")
      if (nameEl) {
        data[key] = nameEl.textContent.trim()
        const dateCell = tr.querySelector("td:last-child")
        data[`${key}_date`] = dateCell ? dateCell.textContent.trim() : ""
      }
    })

    return data
  }

  _collectNrsTableRows(table) {
    if (!table) return []
    const rows = []
    table.querySelectorAll("tbody tr").forEach(tr => {
      const cells = tr.querySelectorAll(".nrs-dyn-cell")
      rows.push(Array.from(cells).map(c => c.value || c.textContent.trim()))
    })
    return rows
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _updateRowCells(row, { completed, date, userName, notes, dueDate }) {
    if (!row) return

    // Update data attributes
    row.dataset.completed = String(!!completed)
    if (date !== undefined) row.dataset.date = date || ""
    if (notes !== undefined) row.dataset.notes = notes || ""
    if (dueDate !== undefined) row.dataset.dueDate = dueDate || ""

    // Update checkbox
    const checkbox = row.querySelector('input[type="checkbox"][data-action*="toggleComplete"], input[type="checkbox"][data-action*="toggleCustomComplete"]')
    if (checkbox) checkbox.checked = !!completed

    // Update visible cells
    const dateCell = row.querySelector('[data-field="date"]')
    if (dateCell && date !== undefined) dateCell.textContent = date ? this._formatDate(date) : ""

    const dueDateCell = row.querySelector('[data-field="due-date"]')
    if (dueDateCell && dueDate !== undefined) dueDateCell.textContent = dueDate ? this._formatDate(dueDate) : ""

    const nameCell = row.querySelector('[data-field="user-name"]')
    if (nameCell && userName !== undefined) nameCell.textContent = userName

    const notesCell = row.querySelector('[data-field="notes"]')
    if (notesCell && notes !== undefined) {
      notesCell.textContent = notes
      notesCell.title = notes || ""
    }
  }

  _updateNaRow(row, na) {
    if (!row) return
    row.dataset.na = String(na)

    // Toggle Completed checkbox visibility
    const completedCb = row.querySelector('input[type="checkbox"][data-action*="toggleComplete"], input[type="checkbox"][data-action*="toggleCustomComplete"]')
    if (completedCb) {
      if (na) {
        completedCb.hidden = true
        completedCb.checked = false
      } else {
        completedCb.hidden = false
      }
    }

    // When marking N/A, clear completion date (model callback clears it server-side)
    if (na) {
      row.dataset.completed = "false"
      row.dataset.date = ""
      const dateCell = row.querySelector('[data-field="date"]')
      if (dateCell) dateCell.textContent = ""
    }
  }

  _syncRowAttachments(row, attachments) {
    if (!row) return

    // Use server-provided attachment data (array of {id, name, url})
    const list = attachments || []
    const allIds = list.map(a => a.id).join(" ")
    const allNames = list.map(a => a.name)
    const allUrls = list.map(a => a.url || "")
    row.dataset.attachmentIds = allIds
    row.dataset.attachmentNames = allNames.join("||")
    row.dataset.attachmentUrls = allUrls.join("||")

    // Show or hide paperclip icon
    const cell = row.querySelector('[data-field="attachments"]')
    if (!cell) return

    if (allNames.length > 0 && !cell.querySelector("a")) {
      const a = document.createElement("a")
      a.href = "#"
      a.dataset.action = "real-estate-steps#viewAttachments"
      a.className = "text-blue-400 hover:text-blue-600"
      a.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mx-auto"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"/></svg>`
      cell.appendChild(a)
    } else if (allNames.length === 0) {
      cell.innerHTML = ""
    }
  }

  _formatDate(isoDate) {
    if (!isoDate) return ""
    const d = new Date(isoDate + "T00:00:00")
    if (isNaN(d)) return isoDate
    const lang = document.documentElement.lang || "en"
    return new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", year: "numeric" }).format(d)
  }

  _updateSigRow(signType, opts) {
    const row = this.sigRowTargets.find(r => r.dataset.signType === signType)
    if (!row) return

    const checkboxTd = row.querySelector('[data-field="checkbox"]')
    const dateTd = row.querySelector('[data-field="date"]')
    const sigTd = row.querySelector('[data-field="signature"]')
    const nameTd = row.querySelector('[data-field="name"]')
    const notesTd = row.querySelector('[data-field="notes"]')
    const titleTd = row.querySelector('[data-field="title"]')

    if (opts.signed) {
      const titleName = (opts.name || "").split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ")
      checkboxTd.innerHTML = `<div class="flex items-center justify-center h-full">
        <input type="checkbox" checked class="form-checkbox h-4 w-4 text-blue-500 rounded"
               data-action="real-estate-steps#unsignPreferredProperty"
               data-sign-type="${signType}"
               data-signer-id="${this.currentUserIdValue}"
               title="Click to unsign">
      </div>`
      const today = new Date()
      dateTd.textContent = this._formatDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`)
      sigTd.innerHTML = `<span style="font-family: 'Allura', cursive; font-size: 20px; line-height: 20px;">${titleName}</span>`
      nameTd.textContent = titleName
      notesTd.textContent = opts.notes || ""
      // Remove click action from title (already signed)
      titleTd.removeAttribute("data-action")
      titleTd.removeAttribute("data-sign-type")
      // title stays visually clickable
    } else {
      checkboxTd.innerHTML = `<div class="flex items-center justify-center h-full">
        <input type="checkbox" class="form-checkbox h-4 w-4 text-blue-500 rounded"
               data-action="real-estate-steps#signPreferredProperty"
               data-sign-type="${signType}">
      </div>`
      dateTd.textContent = ""
      sigTd.innerHTML = ""
      nameTd.textContent = ""
      notesTd.textContent = ""
      // Re-enable click action on title
      titleTd.setAttribute("data-action", "click->real-estate-steps#signPreferredProperty")
      titleTd.setAttribute("data-sign-type", signType)
      // cursor-pointer already on from CSS
    }
  }

  _post(url, data) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": token,
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify(data)
    })
  }

  _insertCustomStepRow(step) {
    if (!this.hasCustomStepsContainerTarget) return
    // In DD, there are two customStepsContainer targets:
    // index 0 = contract dates, index 1 = regular steps
    const containers = this.customStepsContainerTargets
    let container
    if (containers.length > 1 && step.dd_type && step.dd_type !== "contract_date") {
      container = containers[1]
    } else {
      container = containers[0]
    }

    let tbody = container.querySelector("tbody")

    if (!tbody) {
      // No custom steps table yet — create one with colgroup matching standard table
      const tableWrapper = document.createElement("div")
      tableWrapper.className = "mt-5"
      const table = document.createElement("table")
      table.className = "w-full text-sm border-collapse table-fixed"
      const showOD = this.stageNameValue === "Due Diligence" || this.stageNameValue === "Negotiations"
      const hasNA = this.element.querySelector('input[data-action*="toggleCustomNa"], input[data-action*="toggleNa"]') !== null
      let cols = '<colgroup><col class="w-[80px]"><col class="w-[20%]"><col class="w-[30px]">'
      if (showOD) cols += '<col class="w-[10%]">'
      cols += '<col class="w-[10%]"><col class="w-[13%]"><col>'
      if (hasNA) cols += '<col class="w-[80px]">'
      cols += '</colgroup>'
      table.innerHTML = cols
      tbody = document.createElement("tbody")
      table.appendChild(tbody)
      tableWrapper.appendChild(table)
      container.appendChild(tableWrapper)
    }

    const showOrderDate = this.stageNameValue === "Due Diligence" || this.stageNameValue === "Negotiations"
    const hasNaCol = this.element.querySelector('input[data-action*="toggleCustomNa"], input[data-action*="toggleNa"]') !== null

    const responsiblesIcon = step.responsibles
      ? `<span class="ml-1 text-gray-400 cursor-help shrink-0" data-tip="Responsible Party: ${step.responsibles.replace(/\|\|/g, ", ")}">` +
        `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 inline"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" /></svg></span>`
      : ""

    const tr = document.createElement("tr")
    tr.className = "bg-white border border-gray-200 text-center text-xs"
    tr.dataset.stepId = step.id
    tr.dataset.realEstateStepsTarget = "customStepRow"
    tr.dataset.creatorId = ""
    tr.dataset.stepName = step.name
    tr.dataset.completed = "false"
    tr.dataset.na = "false"
    tr.dataset.notes = ""
    tr.dataset.date = ""
    tr.dataset.dueDate = ""
    tr.dataset.responsibles = step.responsibles || ""
    tr.dataset.attachmentIds = ""
    tr.dataset.ddType = step.dd_type || ""
    tr.dataset.isCustom = "true"
    tr.dataset.attachmentNames = ""
    tr.dataset.attachmentUrls = ""

    let html = `
      <td class="px-2 py-1 border-r border-gray-200">
        <div class="flex items-center justify-center h-full">
          <input type="checkbox" class="form-checkbox h-4 w-4 text-blue-500 rounded"
                 data-action="real-estate-steps#toggleCustomComplete"
                 data-step-id="${step.id}">
        </div>
      </td>
      <td class="px-2 py-1 border-r border-gray-200 text-left relative font-medium text-blue-400 cursor-pointer"
          data-action="click->real-estate-steps#openCustomStepModal">
        <div class="flex items-center gap-1">
          <span class="truncate" title="${step.name}">${step.name}</span>${responsiblesIcon}
        </div>
      </td>
      <td class="px-2 py-1 border-r border-gray-200" data-field="attachments"></td>`

    if (showOrderDate) {
      html += `<td class="px-2 py-1 border-r border-gray-200"></td>`
    }

    html += `
      <td class="px-2 py-1 border-r border-gray-200" data-field="date"></td>
      <td class="px-2 py-1 border-r border-gray-200 truncate" data-field="user-name"></td>
      <td class="px-2 py-1 border-r border-gray-200 truncate text-left" data-field="notes"></td>`

    if (hasNaCol) {
      html += `
        <td class="px-2 py-1 border-r border-gray-200">
          <div class="flex items-center justify-center h-full">
            <input type="checkbox" class="form-checkbox h-4 w-4 text-blue-500 rounded"
                   data-action="real-estate-steps#toggleCustomNa"
                   data-step-id="${step.id}">
          </div>
        </td>`
    }

    tr.innerHTML = html
    tbody.appendChild(tr)
  }

  // ── Inline update helpers (no page reload) ─────────────────────────

  _appendLogEntry(data) {
    if (!data.log_id || !data.log_text) return
    const logText = data.log_text || ""
    const firstDash = logText.indexOf(" - ")
    const firstPart = firstDash >= 0 ? logText.substring(0, firstDash) : logText
    const rest = firstDash >= 0 ? logText.substring(firstDash + 3) : ""

    const p = document.createElement("p")
    p.className = "text-[12px] text-gray-400 mb-0.5"
    p.dataset.logId = data.log_id
    p.textContent = `${firstPart} - ${rest}`

    if (this.hasHistoryLogSectionTarget) this.historyLogSectionTarget.removeAttribute("hidden")
    if (this.hasHistoryLogTarget) this.historyLogTarget.prepend(p)
  }

  _applyPreferredProperty(data) {
    const ppId = String(data.pp_id)

    // Highlight the selected property row in both purchase and lease tables
    document.querySelectorAll("tr[data-pp-id]").forEach(row => {
      if (row.dataset.ppId === ppId) {
        row.className = row.className.replace(/border\S*/g, "").replace(/bg-\S*/g, "").trim()
        row.className += " bg-[rgba(40,167,69,0.10)] border-2 border-[rgba(40,167,69,0.4)] text-center"
      } else {
        row.className = row.className.replace(/border-2\s*/g, "").replace(/border-\[rgba\(40,167,69,0\.4\)\]/g, "")
        if (!row.className.includes("border ")) {
          row.className = row.className.replace(/bg-\S*/g, "").trim()
          row.className += " bg-white border border-t-0 border-gray-200 text-center"
        }
      }
    })

    // Update Preferred Property row
    if (this.hasPpRowTarget) {
      const row = this.ppRowTarget
      const fmt = (v) => {
        const n = parseFloat(v) || 0
        return n === 0 ? "$0" : "$" + Math.round(n).toLocaleString("en-US")
      }
      const ppStr = `${data.pp_num} - ${data.pp_name} (${fmt(data.pp_total)})`

      const dateCell = row.querySelector('[data-field="date"]')
      if (dateCell) dateCell.textContent = data.date

      const propCell = row.querySelector('[data-field="property"]')
      if (propCell) propCell.textContent = ppStr

      const nameCell = row.querySelector('[data-field="name"]')
      if (nameCell) nameCell.textContent = data.user_name?.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ") || ""

      const sigCell = row.querySelector('[data-field="signature"]')
      if (sigCell) sigCell.innerHTML = `<span style="font-family: 'Allura', cursive; font-size: 20px; line-height: 20px;">${nameCell.textContent}</span>`

      const notesCell = row.querySelector('[data-field="notes"]')
      if (notesCell) notesCell.textContent = data.notes || ""
    }

    // Update PP checkbox to "checked" and switch action to unselect
    if (this.hasPpCheckboxTarget) {
      const cb = this.ppCheckboxTarget
      cb.checked = true
      cb.dataset.action = "real-estate-steps#deletePreferredProperty"
      cb.title = "Click to unselect preferred property"
    }

    // Show signatures section and reset signature rows
    if (this.hasSignaturesPlaceholderTarget) {
      this.signaturesPlaceholderTarget.setAttribute("hidden", "")
    }
    if (this.hasSignaturesSectionTarget) {
      this.signaturesSectionTarget.removeAttribute("hidden")
      this.sigRowTargets.forEach(sigRow => {
        const checkbox = sigRow.querySelector('input[type="checkbox"]')
        if (checkbox) {
          checkbox.checked = false
          checkbox.removeAttribute("disabled")
          checkbox.className = "form-checkbox h-4 w-4 text-blue-500 rounded"
          checkbox.dataset.action = "real-estate-steps#signPreferredProperty"
          checkbox.dataset.signType = sigRow.dataset.signType
        }
        const sigCell = sigRow.querySelector('[data-field="signature"]')
        if (sigCell) sigCell.innerHTML = ""
        const dateCell = sigRow.querySelector('[data-field="date"]')
        if (dateCell) dateCell.textContent = ""
        const nameCell = sigRow.querySelector('[data-field="name"]')
        if (nameCell) nameCell.textContent = ""
        const notesCell = sigRow.querySelector('[data-field="notes"]')
        if (notesCell) notesCell.textContent = ""

        // Re-enable title click
        const titleCell = sigRow.querySelector('[data-field="title"]')
        if (titleCell) {
          titleCell.dataset.action = "click->real-estate-steps#signPreferredProperty"
          titleCell.dataset.signType = sigRow.dataset.signType
        }
      })
    }
  }

  _clearPreferredProperty() {
    // Remove green highlight from all property rows
    document.querySelectorAll("tr[data-pp-id]").forEach(row => {
      row.className = row.className.replace(/border-2\s*/g, "").replace(/border-\[rgba\(40,167,69,0\.4\)\]/g, "")
      row.className = row.className.replace(/bg-\S*/g, "").trim()
      row.className += " bg-white border border-t-0 border-gray-200 text-center"
    })

    // Clear Preferred Property row
    if (this.hasPpRowTarget) {
      const row = this.ppRowTarget
      ;["date", "property", "name", "signature", "notes"].forEach(field => {
        const cell = row.querySelector(`[data-field="${field}"]`)
        if (cell) cell.textContent = ""
      })
    }

    // Uncheck PP checkbox and switch action to select
    if (this.hasPpCheckboxTarget) {
      const cb = this.ppCheckboxTarget
      cb.checked = false
      cb.dataset.action = "real-estate-steps#openSelectPp"
      cb.title = "Select preferred property"
    }

    // Hide signatures section, show placeholder
    if (this.hasSignaturesSectionTarget) {
      this.signaturesSectionTarget.setAttribute("hidden", "")
    }
    if (this.hasSignaturesPlaceholderTarget) {
      this.signaturesPlaceholderTarget.removeAttribute("hidden")
    }
  }

  _syncPreferredPropertyRow(row) {
    if (!row || !row.className.includes("border-[rgba(40,167,69,0.4)]")) return
    if (!this.hasPpRowTarget) return
    const propCell = this.ppRowTarget.querySelector('[data-field="property"]')
    if (!propCell) return

    const idx = row.querySelector("td")?.textContent.trim() || ""
    const name = row.dataset.ppName || ""
    const type = row.dataset.propertyType
    const p = (v) => parseFloat(v) || 0
    const total = type === "purchase"
      ? p(row.dataset.ppPurchasePrice) + p(row.dataset.ppOnSiteCosts) + p(row.dataset.ppOffSiteCosts)
      : p(row.dataset.ppRentalRate) + p(row.dataset.ppTenantImprovements) + p(row.dataset.ppDesignAndPermitCosts)
    const fmtTotal = total === 0 ? "$0" : "$" + Math.round(total).toLocaleString("en-US")
    propCell.textContent = `${idx} - ${name} (${fmtTotal})`
  }

  _renumberPropertyRows() {
    // Rebuild sequential numbers across both tables and sync dropdown options
    let globalIdx = 0
    const reindex = (tbody) => {
      if (!tbody) return
      tbody.querySelectorAll("tr[data-pp-id]").forEach(row => {
        globalIdx++
        const numCell = row.querySelector("td")
        if (numCell) numCell.textContent = globalIdx

        // Sync dropdown option text
        if (this.hasSelectPpPropertySelectTarget) {
          const opt = this.selectPpPropertySelectTarget.querySelector(`option[value="${row.dataset.ppId}"]`)
          if (opt) opt.textContent = `${globalIdx} - ${row.dataset.ppName || ""}`
        }
      })
    }
    if (this.hasPurchaseTableBodyTarget) reindex(this.purchaseTableBodyTarget)
    if (this.hasLeaseTableBodyTarget) reindex(this.leaseTableBodyTarget)
  }

  _insertPropertyRow(pp) {
    const type = pp.property_type
    const tbody = type === "purchase"
      ? (this.hasPurchaseTableBodyTarget ? this.purchaseTableBodyTarget : null)
      : (this.hasLeaseTableBodyTarget ? this.leaseTableBodyTarget : null)
    if (!tbody) return

    const idx = tbody.querySelectorAll("tr").length + 1
    const fmt = (v) => {
      const n = parseFloat(v) || 0
      return n === 0 ? "$0" : "$" + Math.round(n).toLocaleString("en-US")
    }

    const tr = document.createElement("tr")
    tr.className = "bg-white border border-t-0 border-gray-200 text-center"
    tr.dataset.ppId = pp.id
    tr.dataset.propertyType = type
    tr.dataset.ppName = pp.name || ""
    tr.dataset.ppStatus = pp.status || ""
    tr.dataset.ppAcres = pp.acres || ""
    tr.dataset.ppAddress = pp.address || ""
    tr.dataset.ppCity = pp.city || ""
    tr.dataset.ppState = pp.state || ""
    tr.dataset.ppCountry = pp.country || ""
    tr.dataset.ppZipcode = pp.zipcode || ""
    tr.dataset.ppPurchasePrice = pp.purchase_price || ""
    tr.dataset.ppNotes = pp.notes || ""
    tr.dataset.ppRsf = pp.rental_square_footage || ""
    tr.dataset.ppLeaseRate = pp.lease_rate || ""
    tr.dataset.ppCam = pp.common_area_maintenance || ""
    tr.dataset.ppLeaseTerm = pp.lease_term || ""
    tr.dataset.ppRentalRate = pp.rental_rate || ""
    tr.dataset.ppOnSiteCosts = pp.on_site_costs || ""
    tr.dataset.ppOffSiteCosts = pp.off_site_costs || ""
    tr.dataset.ppTenantImprovements = pp.tenant_improvements || ""
    tr.dataset.ppDesignAndPermitCosts = pp.design_and_permit_costs || ""
    tr.dataset.ppSlab = pp.slab || ""
    tr.dataset.ppEarthwork = pp.earthwork || ""
    tr.dataset.ppSiteUtilities = pp.site_utilities || ""
    tr.dataset.ppAsphalt = pp.asphalt || ""
    tr.dataset.ppFencing = pp.fencing || ""
    tr.dataset.ppLandscaping = pp.landscaping || ""
    tr.dataset.attachmentIds = pp.attachment_ids || ""
    tr.dataset.attachmentNames = pp.attachment_names || ""
    tr.dataset.attachmentUrls = pp.attachment_urls || ""

    const hasAtts = (pp.attachment_ids || "").trim().length > 0
    const clipSvg = `<svg class="w-3.5 h-3.5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" /></svg>`
    const attCell = hasAtts
      ? `<a href="#" data-action="click->real-estate-steps#viewPropertyAttachments" class="text-blue-400 hover:text-blue-600">${clipSvg}</a>`
      : ""
    const trashSvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>`

    if (type === "purchase") {
      const total = (parseFloat(pp.on_site_costs) || 0) + (parseFloat(pp.off_site_costs) || 0) + (parseFloat(pp.purchase_price) || 0)
      tr.innerHTML = `
        <td class="px-2 py-1 border-r border-gray-200 font-medium text-blue-400 cursor-pointer" data-action="click->real-estate-steps#editProperty">${idx}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-left truncate max-w-[120px] text-blue-400 cursor-pointer" data-action="click->real-estate-steps#editProperty" title="${pp.name || ""}">${pp.name || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${attCell}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-left truncate max-w-[220px]">${pp.address || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${pp.city || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${pp.state || ""} ${pp.zipcode || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${pp.acres || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${pp.status || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${fmt(pp.purchase_price)}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-blue-400 cursor-pointer" data-action="click->real-estate-steps#openEditCost" data-cost-type="on_site" data-pp-id="${pp.id}">${fmt(pp.on_site_costs)}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-blue-400 cursor-pointer" data-action="click->real-estate-steps#openEditCost" data-cost-type="off_site" data-pp-id="${pp.id}">${fmt(pp.off_site_costs)}</td>
        <td class="px-2 py-1 border-r border-gray-200 font-medium">${fmt(total)}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-left truncate max-w-[100px]" title="${pp.notes || ""}">${pp.notes || ""}</td>
        <td class="px-2 py-1"><button class="text-red-500 hover:text-red-700" data-action="real-estate-steps#deleteProperty" data-pp-id="${pp.id}" title="Delete">${trashSvg}</button></td>`
    } else {
      const leaseTotal = (parseFloat(pp.rental_rate) || 0) + (parseFloat(pp.tenant_improvements) || 0) + (parseFloat(pp.design_and_permit_costs) || 0)
      tr.innerHTML = `
        <td class="px-2 py-1 border-r border-gray-200 font-medium text-blue-400 cursor-pointer" data-action="click->real-estate-steps#editProperty">${idx}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-left truncate max-w-[120px] text-blue-400 cursor-pointer" data-action="click->real-estate-steps#editProperty" title="${pp.name || ""}">${pp.name || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${attCell}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-left truncate max-w-[220px]">${pp.address || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${pp.city || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${pp.state || ""} ${pp.zipcode || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${pp.rental_square_footage || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200">${pp.status || ""}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-blue-400 cursor-pointer" data-action="click->real-estate-steps#openEditCost" data-cost-type="rental_rate" data-pp-id="${pp.id}">${fmt(pp.rental_rate)}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-blue-400 cursor-pointer" data-action="click->real-estate-steps#openEditCost" data-cost-type="tenant_improvements" data-pp-id="${pp.id}">${fmt(pp.tenant_improvements)}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-blue-400 cursor-pointer" data-action="click->real-estate-steps#openEditCost" data-cost-type="design_and_permit" data-pp-id="${pp.id}">${fmt(pp.design_and_permit_costs)}</td>
        <td class="px-2 py-1 border-r border-gray-200 font-medium">${fmt(leaseTotal)}</td>
        <td class="px-2 py-1 border-r border-gray-200 text-left truncate max-w-[100px]" title="${pp.notes || ""}">${pp.notes || ""}</td>
        <td class="px-2 py-1"><button class="text-red-500 hover:text-red-700" data-action="real-estate-steps#deleteProperty" data-pp-id="${pp.id}" title="Delete">${trashSvg}</button></td>`
    }

    tbody.appendChild(tr)

    // Show the Preferred Property section if this is the first property added
    const purchaseCount = this.hasPurchaseTableBodyTarget ? this.purchaseTableBodyTarget.querySelectorAll("tr").length : 0
    const leaseCount = this.hasLeaseTableBodyTarget ? this.leaseTableBodyTarget.querySelectorAll("tr").length : 0
    if (purchaseCount + leaseCount === 1) {
      if (this.hasPreferredPropertySectionTarget) this.preferredPropertySectionTarget.hidden = false
      if (this.hasPreferredPropertyPlaceholderTarget) this.preferredPropertyPlaceholderTarget.hidden = true
    }

    // Add to "Select Preferred Property" dropdown
    if (this.hasSelectPpPropertySelectTarget) {
      const opt = document.createElement("option")
      opt.value = pp.id
      opt.textContent = `${idx} - ${pp.name || ""}`
      this.selectPpPropertySelectTarget.appendChild(opt)
    }
  }

  _showToast(message) {
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _showField(el, show) {
    if (el) el.style.display = show ? "" : "none"
  }

  _lockScroll() {
    this._openModalCount++
    document.body.style.overflow = "hidden"
  }

  _unlockScroll() {
    this._openModalCount = Math.max(0, this._openModalCount - 1)
    if (this._openModalCount === 0) document.body.style.overflow = ""
  }

  _setDatepicker(el, value) {
    if (el._flatpickr) {
      el._flatpickr.setDate(value || null, false)
    } else {
      el.value = value || ""
    }
  }

  _getSelectedResponsibles() {
    return this._editSelectedResponsibles
      ? Array.from(this._editSelectedResponsibles.keys()).join("||")
      : ""
  }

  _buildAttachmentIds() {
    const existing = this._existingAttachmentIds || []
    return existing.concat(this._uploadedAttachmentIds).join(" ")
  }

  _uploadFilesGeneric(event, callback, listTarget) {
    const files = event.currentTarget.files
    if (!files.length) return
    this._doUpload(files, callback, listTarget)
  }

  async _doUpload(files, callback, listTarget, namesArray = null, urlsArray = null, stepId = null) {
    const fileEntries = Array.from(files)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    // Disable save button while uploading
    const saveBtn = this._findModalSaveBtn(listTarget)
    this._pendingUploads = (this._pendingUploads || 0) + 1
    if (saveBtn) { saveBtn.disabled = true; saveBtn.classList.add("opacity-50", "cursor-not-allowed") }

    // Show uploading placeholders immediately
    const placeholders = fileEntries.map(f => {
      const chip = this._createFileChip(f.name, { removable: false, uploading: true })
      listTarget.appendChild(chip)
      return chip
    })

    try {
      // Phase 0: Compress images client-side (non-images pass through unchanged)
      const prepared = await Promise.all(fileEntries.map(f => this._prepareFile(f)))

      // Phase 1: Get presigned PUT URLs from server
      const presignRes = await fetch(`/real_estate/${this.projectIdValue}/presign_uploads`, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: prepared.map(f => ({
            filename: f.name,
            content_type: f.blob.type || "application/octet-stream",
            byte_size: f.blob.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      // Phase 2: Upload each file directly to S3 in parallel
      const results = await Promise.allSettled(
        uploads.map((upload, i) =>
          fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": prepared[i].blob.type || upload.content_type },
            body: prepared[i].blob
          }).then(r => {
            if (!r.ok) throw new Error(`S3 upload failed: ${r.status}`)
            return upload
          })
        )
      )

      // Collect only successful uploads
      const successful = results
        .filter(r => r.status === "fulfilled")
        .map(r => r.value)

      if (successful.length === 0) throw new Error("All uploads failed")

      // Phase 3: Register successful uploads with server
      const registerRes = await fetch(`/real_estate/${this.projectIdValue}/register_uploads`, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename })),
          step_id: stepId
        })
      })

      const data = await registerRes.json()
      const ids = data.map(a => a.id)
      callback(ids)

      // Replace placeholders with final chips
      placeholders.forEach(p => p.remove())
      data.forEach(a => {
        const name = a.filename || `File #${a.id}`
        if (namesArray) namesArray.push(name)
        if (urlsArray && a.url) urlsArray.push(a.url)
        const chip = this._createFileChip(name, {
          removable: true,
          attachmentId: a.id,
          idsArray: this._getIdsArrayForTarget(listTarget),
          namesArray,
          urlsArray,
          url: a.url
        })
        listTarget.appendChild(chip)
      })
    } catch (e) {
      console.error("Direct upload failed:", e)
      placeholders.forEach(p => p.remove())
      alert(`Upload failed: ${e.message}`)
    } finally {
      this._pendingUploads = Math.max(0, (this._pendingUploads || 0) - 1)
      if (this._pendingUploads === 0 && saveBtn) {
        saveBtn.disabled = false
        saveBtn.classList.remove("opacity-50", "cursor-not-allowed")
      }
    }
  }

  _isCompressibleImage(file) {
    return /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type)
  }

  _prepareFile(file) {
    if (!this._isCompressibleImage(file)) {
      return Promise.resolve({ name: file.name, blob: file })
    }
    return this._compressImage(file).then(blob => ({ name: file.name, blob }))
  }

  _compressImage(file, maxDim = 2000, quality = 0.80) {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        let { width, height } = img

        // Only downscale, never upscale
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height)
          width = Math.round(width * ratio)
          height = Math.round(height * ratio)
        }

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(
          (blob) => resolve(blob || file),
          "image/jpeg",
          quality
        )
      }
      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        resolve(file)
      }
      img.src = URL.createObjectURL(file)
    })
  }

  _getIdsArrayForTarget(listTarget) {
    if (this.hasFileListTarget && listTarget === this.fileListTarget) return this._uploadedAttachmentIds
    if (this.hasPpFileListTarget && listTarget === this.ppFileListTarget) return this._ppAttachmentIds
    if (this.hasEditCostFileListTarget && listTarget === this.editCostFileListTarget) return this._costAttachmentIds
    if (this.hasFcwFileListTarget && listTarget === this.fcwFileListTarget) return this._fcwAttachmentIds
    if (this.hasNrsFileListTarget && listTarget === this.nrsFileListTarget) return this._nrsAttachmentIds
    return null
  }

  _findModalSaveBtn(listTarget) {
    const modal = listTarget.closest(".fixed.inset-0")
    if (!modal) return null
    return modal.querySelector("button.bg-brand, button.btn-primary")
  }

  _createFileChip(name, { removable = false, uploading = false, attachmentId = null, idsArray = null, namesArray = null, urlsArray = null, url = null } = {}) {
    const chip = document.createElement("div")
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? 'pr-1' : 'pr-2'} py-1 rounded-md text-xs ${uploading ? 'bg-blue-50 text-blue-400' : 'bg-gray-100 text-gray-700'}`

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${name}">${truncatedName}</span>${removeBtn}`

    if (removable && attachmentId) {
      const btn = chip.querySelector("[data-remove-chip]")
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        chip.remove()
        if (idsArray) {
          const idx = idsArray.indexOf(attachmentId)
          if (idx > -1) idsArray.splice(idx, 1)
        }
        if (namesArray) {
          const nIdx = namesArray.indexOf(name)
          if (nIdx > -1) namesArray.splice(nIdx, 1)
        }
        if (urlsArray && url) {
          const uIdx = urlsArray.indexOf(url)
          if (uIdx > -1) urlsArray.splice(uIdx, 1)
        }
      })
    }

    return chip
  }

}
